import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { PREVIEW_EDGE, PREVIEW_QUALITY, clampPreviewSize, estimatedCopyBytes, type PreviewSize } from "@/lib/uploader/previewCopy";
import { useQueryClient } from "@tanstack/react-query";
import {
  uploadToPresigned,
  uploadPutPresigned,
  uploadViaWorker,
  uploadWorkerWanted,
  noteUploadWorker,
  uploadMultipart,
  multipartEnabled,
  multipartPartCount,
  relativePath,
  listPendingOriginals,
  listSoldOriginals,
  pollCropBack,
  putArtifact,
  uploadBundle,
  uploadBundleWanted,
  type BundleTarget,
  type PresignedPost,
  type PrescanTargets,
  type MultipartPlan,
  UPLOADER_API_TIMEOUT_MS,
} from "@/lib/api/content";
import { photohawkApi, isSessionExpired, isTransientApiError, PhotohawkApiError } from "@/lib/photohawkApi";
import { cognitoGetSession } from "@/lib/cognito";
import { Signal, waitOnline } from "@/lib/uploader/signal";
import { tenantHostname } from "@/lib/apiDomain";
import {
  probeScan,
  scanFileForUpload,
  uploadPrescanArtifacts,
  shutdownScanPool,
} from "@/lib/scan/uploadScan";
import { poolSize as scanWorkerPoolSize } from "@/lib/scan/scanConfig";
import { scanLog } from "@/lib/scan/scanUi";
import { resizeToJpeg, resizeForCopy, PreviewCopyError, shutdownResizePool, cutCropsFromOriginal, RESIZE_CONCURRENCY, type ResizeOpts } from "@/lib/image/resizeImage";
import { CropBackTrack, cropBackWanted } from "@/lib/uploader/cropBack";
import {
  registerUploadProbe,
  uploadDiagStart,
  uploadDiagStop,
  uploadDiagCancelBurst,
  diagMark,
  diagMarkReset,
} from "@/lib/diag/uploadDiag";
import type { TenantScope } from "@/lib/api/galleries";
import { reportUploadSession } from "@/lib/api/uploadSessions";
import { soldFirst } from "@/lib/uploader/intake";
import { previewSpeedOn } from "@/lib/uploader/previewSpeed";
import { readError, unreadableReason, NOT_TRIED_UNREADABLE, UNREADABLE_NOTICE, UNREADABLE_STOP_AFTER } from "@/lib/uploader/readable";
import { autoRetryable, clientInfo, countFail, errorName, failKey, failureSummary, mintErrorName, topReasons, uploadErrorName, type FailKey } from "@/lib/uploader/failReasons";
import { copyUploadDiagnostics } from "@/lib/uploader/diagnostics";
import {
  FULL_LIST_EVERY,
  SOLD_POLL_MS,
  LEGACY_SOLD_POLL_MS,
  missingLocally,
  nextSoldPollDelay,
  planCopyPass,
  planHoldOnly,
  planOriginalsPass,
  planSoldByName,
  sameSold,
  soldKeysFor,
  type SoldOriginal,
  type SoldPlan,
} from "@/lib/uploader/soldPriority";
import { cloudSourceOf, transferFromCloud, CloudReconnectError, PROVIDER_LABELS } from "@/lib/api/connections";
import { toast } from "@/hooks/use-toast";
import { nextConcurrency, type ConcTuner } from "./concTuner";
import { planLimitOf } from "@/lib/plans/entitlements";
import { toast as sonnerToast } from "sonner";

/** Gallery scan context: whether on-device prescanning is allowed for this gallery. */
export interface ScanContext {
  enabled: boolean;
  needsPeople: boolean;
  /** Detect faces on-device (selfieSearch). False → people-only prescan (text galleries). */
  needsFaces?: boolean;
}

// Files per presign (mint) request — moderate: few round-trips, still near the frontier.
const MINT_BATCH = 75;
// How many scan coroutines pump files through the on-device detector. Each one
// spends part of its life NOT on inference — awaiting the file's mint gate and
// PUTting its crops/sidecar — so we run a couple more coroutines than there are
// scan workers, keeping every worker's inference busy through those network gaps.
// (The old fixed value of 2 left half of a 4-worker pool idle, so scanning ran at
// a fraction of the machine's capacity and fell far behind a fast upload.)
const scanCoroutineCount = () => {
  try {
    return Math.max(2, Math.min(8, scanWorkerPoolSize() + 2));
  } catch {
    return 4; // navigator unavailable (SSR/probe) — a safe default
  }
};
// How far minting runs ahead of the uploader — modest: buffers URLs so upload
// workers never wait on a mint round-trip, without minting the whole batch up front.
const MINT_LOOKAHEAD = 150;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
/** Is there still a usable sign-in? Forces a refresh, so an expired-but-refreshable
 *  session counts as alive and only a dead refresh token reads as signed out. */
const sessionAlive = async (): Promise<boolean> => {
  const s = await cognitoGetSession(true);
  return !!s?.tokens?.idToken;
};

// A minted content URL now carries its server `contentGuid` at top level — the scan
// stream uses it to address that object's crop/sidecar targets. Large files (videos)
// may instead carry a `multipart` plan (presigned parts + complete/abort) when the
// mint was asked for partCount > 1.
// Preview-first 'original' mints may instead answer `skip: true` for a file: its original is
// already in place, so there is nothing to upload (the file counts as completed).
type MintUrl = PresignedPost & { contentGuid?: string; multipart?: MultipartPlan; skip?: boolean };

/** Preview-first pass: 'copy' uploads a quick 1200px copy the gallery goes live from;
 *  'original' then uploads the untouched originals into the same keys (no rescan). */
export type PreviewFirst = "copy" | "original";
/** Long edge and quality of the preview-first copy: src/lib/uploader/previewCopy.ts (shared with the
 *  upload-time estimator). */
export { PREVIEW_EDGE };
/** The quick copy's resize — the shared just-in-time resize (and the scan) use it. The uploader may pick
 *  another size (EnqueueArgs.previewSize), always clamped to 640–2048px / q0.70–0.95. */
const previewCopyResize = (size?: PreviewSize): ResizeOpts => clampPreviewSize(size ?? { maxEdge: PREVIEW_EDGE, quality: PREVIEW_QUALITY });
/** The copy when the server did NOT accept crop-back for it (mint without `cropBack: true` — crop-back
 *  off on that backend, or turned off in this browser): face / person crops are then cut from the copy
 *  itself, so keep the pre-crop-back 2048px copy rather than lose crop resolution. */
export const PREVIEW_EDGE_NO_CROP_BACK = 2048;
const PREVIEW_COPY_RESIZE_NO_CROP_BACK: ResizeOpts = { maxEdge: PREVIEW_EDGE_NO_CROP_BACK, quality: PREVIEW_QUALITY };

interface MintResponse {
  upload?: { urls?: MintUrl[]; prescanAvailable?: boolean; cropBack?: boolean; bundle?: BundleTarget };
}

// Content mint. In scan mode we ask for DEFERRED prescan: the server marks each object
// so ingestion holds server-side scanning while the on-device sidecar (uploaded later by
// the scan pump) catches up. Each returned url carries its server `contentGuid`, which the
// scan pump uses to address that object's crop/sidecar targets. NEVER waits for any scan.
const presignChunk = async (
  scope: TenantScope,
  engineGuid: string,
  files: File[],
  paths: string[],
  collectionGuid: string | undefined,
  useScan: boolean,
  signal?: AbortSignal,
  allowMultipart = false,
  previewFirst?: PreviewFirst,
  viaWorker = true,
): Promise<{ urls: MintUrl[]; prescanAvailable: boolean; cropBack?: boolean; bundle?: BundleTarget | null }> => {
  const res = await photohawkApi.post<MintResponse>("/admin/content", {
    service: "admin",
    hostname: tenantHostname(scope),
    signal,
    timeoutMs: UPLOADER_API_TIMEOUT_MS, // a dead connection fails as a timeout (retried), never hangs the pump
    body: {
      engineGuid,
      collectionGuid,
      // Large files request S3 multipart via partCount > 1 (gated + videos only);
      // 0/absent → the server mints a single presigned POST as before.
      files: files.map((f, k) => {
        const partCount = allowMultipart ? multipartPartCount(f.size) : 0;
        const path = paths[k] || relativePath(f);
        return partCount > 1 ? { path, size: f.size, partCount } : { path, size: f.size };
      }),
      prescanMode: useScan ? "deferred" : undefined,
      // Opt in to the server's multipart plan (videos only server-side); without it the mint
      // answers a single presigned POST, as it does for the Studio app.
      multipart: allowMultipart ? "v2" : undefined,
      previewFirst,
      // Photos to R2 get an upload-Worker target too (where the stage has it); `put` stays the fallback.
      uploadVia: viaWorker && uploadWorkerWanted() ? "worker" : undefined,
      // Crop-back: we'll cut the face / person crops from the originals for these copies.
      cropBack: previewFirst === "copy" && cropBackWanted() ? true : undefined,
    },
  });
  return {
    urls: res?.upload?.urls ?? [],
    prescanAvailable: res?.upload?.prescanAvailable === true,
    cropBack: res?.upload?.cropBack === true,
    bundle: res?.upload?.bundle ?? null,
  };
};

interface PrescanTargetsResponse {
  targets?: PrescanTargets[];
  prescanAvailable?: boolean;
}

// Scan stream: request crop + sidecar PUT targets for already-minted content objects,
// addressed by contentGuid + on-device detection counts. This runs OFF the upload critical
// path (the content is already uploading/uploaded), so its round-trips never slow uploads.
const requestPrescanTargets = async (
  scope: TenantScope,
  engineGuid: string,
  collectionGuid: string | undefined,
  items: Array<{ contentGuid: string; faceCount: number; personCount: number }>,
  signal?: AbortSignal,
): Promise<{ targets: PrescanTargets[]; prescanAvailable: boolean }> => {
  const res = await photohawkApi.post<PrescanTargetsResponse>("/admin/content", {
    service: "admin",
    hostname: tenantHostname(scope),
    signal,
    timeoutMs: UPLOADER_API_TIMEOUT_MS,
    body: {
      engineGuid,
      collectionGuid,
      prescanTargets: items.map((it) => ({
        contentGuid: it.contentGuid,
        collectionGuid,
        faceCount: it.faceCount,
        personCount: it.personCount,
      })),
    },
  });
  return {
    targets: res?.targets ?? [],
    prescanAvailable: res?.prescanAvailable === true,
  };
};

// App-level background uploader. Uploads run here (above the router), so they
// keep going while the user navigates the admin — closing the upload dialog or
// leaving the gallery does NOT abort them. A beforeunload guard warns before a
// refresh/close while uploads are in flight, and a floating indicator shows
// progress from anywhere. Mirrors the legacy admin's background upload UX.

export type JobPhase =
  | "presigning"
  | "uploading"
  | "scanning"
  | "done"
  | "error"
  | "cancelled";

export type FileStatus = "queued" | "uploading" | "done" | "error";
/** On-device scan lifecycle for one file (only when the batch scans on-device):
 *  pending → scanning → detected (crops found / none) | skipped (handed to the
 *  server scan because on-device was unavailable or failed). */
export type FileScanStatus = "pending" | "scanning" | "detected" | "skipped";
/** Crop + sidecar upload lifecycle for one file: pending → uploading → done, or
 *  skipped (server will scan) / failed (upload of a crop or the sidecar failed →
 *  the photo falls back to a server scan). */
export type FileSidecarStatus = "pending" | "uploading" | "done" | "skipped" | "failed";
/** Per-file progress, surfaced so the Upload tab can show a live per-file table. */
export interface FileProgress {
  name: string;
  size: number;
  loaded: number;
  status: FileStatus;
  /** On-device scan state (undefined when the batch doesn't scan on-device). */
  scan?: FileScanStatus;
  /** Faces / people detected on-device once this file is scanned. */
  faces?: number;
  people?: number;
  /** Crop + sidecar upload state (undefined when the batch doesn't scan). */
  sidecar?: FileSidecarStatus;
  /** Done without uploading — the server already had this original (preview-first). */
  skipped?: boolean;
  /** Why this file failed, when we know (e.g. its preview copy couldn't be made in this browser). */
  error?: string;
  /** Its failure's class (failReasons.FailKey, e.g. "read:NotReadableError") — the row's short label. */
  reason?: string;
}

export interface UploadJob {
  id: string;
  galleryName: string;
  engineGuid: string;
  scopeKey: string;
  total: number;
  completed: number;
  failed: number;
  bytesTotal: number;
  bytesSent: number;
  phase: JobPhase;
  startedAt: number;
  /** When the batch reached a terminal phase (done/error/cancelled) — drives the
   *  "uploaded in Xm Ys" summary so it can be compared against an FTP client. */
  finishedAt?: number;
  kind: "image" | "video";
  /** Target collection (folder) guid, if uploading into a specific folder. */
  folder?: string;
  /** On-device scan context for this gallery (images only). */
  scan?: ScanContext;
  /** Just-in-time resize options for this batch (images only), or undefined for none. */
  resize?: ResizeOpts;
  /** Preview-first pass this batch is (quick copies / originals), or undefined for a plain upload. */
  previewFirst?: PreviewFirst;
  /** Per-file rows (name/size/loaded/status) for the Upload tab table. */
  files: FileProgress[];
  /** Ordering within a gallery's combined queue view. Lower sorts first;
   *  prioritised batches get a negative seq so they lead, normal ones append. */
  seq: number;
  /** Live adaptive-concurrency state, for surfacing what the tuner is doing. */
  conc?: ConcState;
  /** On-device face/person scan progress. Content upload can finish well before
   *  scanning does, so this drives the "still scanning" state + its ETA. */
  scanTotal?: number;
  scanCompleted?: number;
  /** The sign-in expired mid-run: minting is on hold (already-minted files keep
   *  uploading) until the user signs in again, then the job carries on by itself. */
  waitingForSignIn?: boolean;
  /** User paused this job — the pool holds (finishes in-flight, claims no new
   *  files) until resumed. Orthogonal to `phase` (a job can be paused while
   *  uploading or scanning), so resuming restores the underlying phase cleanly. */
  paused?: boolean;
  // ---- Upload timeline (UI-only, additive; see src/lib/uploader/uploadTimeline.ts) ----
  /** When the first files were ready to send (the batch entered "uploading"). */
  uploadingAt?: number;
  /** When this batch's content uploads drained (on-device scanning may still be running). */
  contentDoneAt?: number;
  /** Links a preview-first copy pass with the originals passes that follow it: the copy job's id. */
  pairId?: string;
  /** How many sold photos lead this originals pass (sent first). */
  soldFirst?: number;
  /** "Upload previews now, originals later" (a copy pass only): the promised "Originals by" (epoch ms). The
   *  originals pass is NOT queued after the copies, and no originals go up during it (not even sold ones). */
  originalsBy?: number;
  /** Failed files per reason (failReasons.FailKey → count): the summary notice, the session report, "Report a problem". */
  failReasons?: Record<string, number>;
  /** Files counted done WITHOUT an upload because the server already had them (preview-first originals). */
  alreadyThere?: number;
  /** The automatic retry of files that didn't go through: how many, which round of how many, and when it
   *  starts (epoch ms; absent once they're being sent). Cleared when the retries are over. */
  retrying?: { count: number; round: number; rounds: number; at?: number };
}

/** Snapshot of the adaptive upload controller, published for the UI. */
export interface ConcState {
  /** Files currently being uploaded in parallel (the controller's target). */
  workers: number;
  /** Whether the tuner just increased, decreased, or held the parallelism. */
  trend: "up" | "down" | "steady";
  /** Most recent measured upload throughput, bytes/sec. */
  bytesPerSec: number;
}

/**
 * Plain-language description of what the adaptive uploader is doing, so users
 * understand it's continuously tuning itself to their connection speed.
 */
export const concMessage = (c?: ConcState): string | null => {
  if (!c || !c.workers) return null;
  const mbps = c.bytesPerSec / (1024 * 1024);
  const speed = mbps > 0.05 ? ` at ~${mbps.toFixed(1)} MB/s` : "";
  const tuning =
    c.trend === "up"
      ? "speeding up"
      : c.trend === "down"
        ? "easing back"
        : "holding steady";
  return `Tuning to your connection — uploading ${c.workers} file${
    c.workers === 1 ? "" : "s"
  } at once${speed} (${tuning}).`;
};

interface EnqueueArgs {
  scope: TenantScope;
  engineGuid: string;
  galleryName?: string;
  files: File[];
  /** "image" (photos) or "video". Controls which files are accepted and which
   * gallery list is refreshed on completion. Defaults to image. */
  kind?: "image" | "video";
  /** Upload into a specific collection (folder) guid. */
  folder?: string;
  /** When set (and enabled), photos are scanned on-device before upload. */
  scan?: ScanContext;
  /** Downscale/re-encode each image just-in-time as it uploads (images only). When
   *  unset (or "original"), files upload untouched. Resizing is pipelined per file
   *  inside the pool, so uploads START immediately instead of waiting for the whole
   *  batch to resize first. */
  resize?: ResizeOpts;
  /** Adding to an in-progress run: place this batch at the START of the gallery's
   *  queue view (prioritised) instead of appending at the end. */
  prioritise?: boolean;
  /** Upload path (key relative to the target folder) per file, aligned with `files`.
   *  Defaults to the file's own relative path / name. Set when a selection spans
   *  subfolders with clashing names so each keeps its folder instead of overwriting. */
  paths?: string[];
  /** Preview-first (images only). 'copy': upload a quick copy (previewCopyResize: 1200px q75 unless previewSize says otherwise,
   *  overriding `resize`) so the gallery goes live fast, then AUTOMATICALLY queue an 'original'
   *  batch for the files that made it. 'original': upload untouched (no resize, no scan) into
   *  the same keys; the server attaches them to the existing photos. */
  previewFirst?: PreviewFirst;
  /** With previewFirst 'copy': the copy's long edge / quality (clamped; default 1200px q75). */
  previewSize?: PreviewSize;
  /** Timeline link to the copy pass this batch belongs to (see UploadJob.pairId). */
  pairId?: string;
  /** Timeline: sold photos leading this originals batch (see UploadJob.soldFirst). */
  soldFirst?: number;
  /** "Upload previews now, originals later": with previewFirst 'copy', upload only the copies — no originals pass
   *  afterwards — and show "originals expected by" this time (epoch ms). */
  originalsBy?: number;
}

/** Preview-first: sold photos (a buyer is waiting) that no running pass in this tab holds — their files
 *  aren't in this upload session (another session, device or folder). Per gallery, from the sold poll. */
export interface SoldWaiting {
  engineGuid: string;
  scopeKey: string;
  /** Sold photos still owed their original that this tab can't send (file not in any running pass). */
  missing: SoldOriginal[];
  /** All sold photos still owed their original in the gallery (held here or not). */
  sold: SoldOriginal[];
  checkedAt: number;
}

/** A running preview-first pass's answer to one sold poll (see src/lib/uploader/soldPriority.ts). */
interface SoldHandler {
  scope: TenantScope;
  scopeKey: string;
  engineGuid: string;
  folder?: string;
  onSold: (sold: Set<string>) => Set<string>;
  /** Sold photos no pass holds by path: send a same-name local file as their original when unambiguous
   *  (soldPriority.planSoldByName). Returns the sold keys this pass now covers. */
  onSoldByName?: (missing: SoldOriginal[], sold: SoldOriginal[]) => Set<string>;
}

interface UploadManagerValue {
  jobs: UploadJob[];
  /** Preview-first sold photos per gallery (`${scopeKey}|${engineGuid}`), while a pass runs there. */
  soldWaiting: Record<string, SoldWaiting>;
  activeCount: number;
  enqueue: (args: EnqueueArgs) => void;
  dismiss: (id: string) => void;
  clearFinished: () => void;
  /** Pause a job (or, with no id, every still-running job). The pool finishes any
   *  in-flight file, then holds without claiming more. */
  pause: (id?: string) => void;
  /** Resume a paused job (or every paused job) from where it left off. */
  resume: (id?: string) => void;
  /** Cancel a job: abort its in-flight uploads, drop its remaining queued files,
   *  and mark it cancelled (terminal — not resumable). */
  cancelJob: (id: string) => void;
  /** Cancel every still-running job. */
  cancelAll: () => void;
  /** Re-upload a finished job's failed files (same destination and paths) as a new,
   *  prioritised batch; the old job is dismissed. */
  retryFailed: (id: string) => void;
  /** True while uploads are on hold because the sign-in expired. */
  sessionExpired: boolean;
  /** Open sign-in in a new tab; uploads resume here by themselves once it succeeds. */
  signInAgain: () => void;
}

/**
 * Per-job control block shared with the async workers. Lives in a ref (not React
 * state) so the pool reads live pause/cancel flags without stale closures, and so
 * cancel can abort every in-flight request. `controllers` holds one AbortController
 * per in-flight PUT / presign request; cancelling aborts them all at once.
 */
interface JobControl {
  paused: boolean;
  cancelled: boolean;
  controllers: Set<AbortController>;
  /** Notified on pause / resume / cancel so waiting loops wake without polling. */
  sig: Signal;
  /** Release this batch's big per-file structures (mint targets, gate promises, the shared
   *  resized-copy cache) at once — runJob sets it; cancel calls it immediately so a cancelled
   *  huge batch doesn't keep them live while the pumps unwind and the GC thrashes. */
  dispose?: () => void;
  /** Set when the upload API refused the batch for a plan reason (from 1 November 2026: over the
   *  storage allowance with no card on file, or video off-plan). Remaining chunks stop minting. */
  blocked?: string;
  /** A preview-first COPY pass the server refused because the workspace's plan doesn't include
   *  previews first (403 PLAN_FEATURE previewFirst — e.g. the plan changed since the uploader loaded):
   *  its photos go up as an ordinary upload instead (originals only). Photos already up as previews
   *  still get their originals — finishing is never refused. */
  previewFirstRefused?: boolean;
}

const UploadManagerContext = createContext<UploadManagerValue | null>(null);

// Adaptive upload concurrency. Instead of a fixed pool, a controller samples the
// achieved throughput and hill-climbs the number of parallel uploads toward the
// point of diminishing returns for THIS connection — a fast, high-latency link
// ramps up (more parallelism hides RTT), a slow or single-connection link settles
// low (extra workers would just contend). The controller only pushes the count
// higher while throughput keeps improving, so it self-limits to whatever the
// connection actually supports; CONC_MAX is just a high safety backstop so a
// pathological case can't exhaust the browser's socket pool or balloon memory
// holding a very large number of files in flight.
const CONC_MIN = 2;
const CONC_MAX = 64;
const CONC_START = 6;
// Dropbox / Google Drive imports: the server copies each file, so there's no browser bandwidth
// to tune for — a fixed, modest parallelism keeps within the providers' rate limits.
const CLOUD_CONC = 6;
const CONC_SAMPLE_MS = 1500; // throughput sampling cadence for the controller
// Small-file batches (preview copies / resized uploads, a few hundred KB each) are latency-bound: a
// 1.5s sample only sees a handful of files finish, so bytes/s swings far more than the old 3% turn-around
// band and the ±1 hill-climb random-walked around its start (previews stuck at ~4–6 in flight even with
// plenty of copies resized ahead). They start wider, step by ~25% and judge a smoothed rate.
const SMALL_CONC_START = 12;
// Over the HTTP/2 upload Worker the uploads in flight settle at (copies per second) × (request time): an
// 8-core machine resizing ~40–50 copies/s against ~0.65 s requests holds ~26 at once on average and
// peaked at 46 (staging bench), so the Worker path starts at 24 (the climb from 12 took ~10 s of a
// ~30 s preview phase) and may go to 64. Extra requests multiplex over the one connection; they don't
// queue behind 6 sockets. (12 → 32 with the preview-speed kill switch, src/lib/uploader/previewSpeed.ts.)
const SMALL_CONC_START_WORKER = 24;
const SMALL_CONC_MAX = 64;
const SMALL_CONC_MAX_LEGACY = 32;

// Bundled Worker uploads (localStorage ph_up_bundle=1): files per POST /b, and how many bundles may be
// in flight (each carries BUNDLE_FILES files, so far fewer than single-file uploads).
const BUNDLE_FILES = 20;
const BUNDLE_CONC_MAX = 6;
// Per-file upload retry. A transient S3/network failure shouldn't drop a photo, so
// each content PUT gets a bounded retry with exponential backoff (mirrors legacy
// photohawk-admin's 3 attempts) before the file is marked failed. A user cancel
// aborts immediately and never retries.
const UPLOAD_ATTEMPTS = 5;
const UPLOAD_BACKOFF_MS = 500; // exponential backoff base (with jitter): ~0.5s, 1s, 2s, 4s
// Mint (presign) retry. A mint covers MINT_BATCH files, so one failed request used to fail
// 75 photos outright — over a 100k run (~1,300 mints) a stray 5xx or network blip was all
// but certain. Transient failures now back off and retry (1s, 2s, 4s … capped at 30s,
// ~1.5 min in all); going offline waits for the network without spending attempts; an
// expired sign-in holds the job until the user signs in again (never fails the files).
const MINT_ATTEMPTS = 8;
const MINT_BACKOFF_MS = 1000;
const MINT_BACKOFF_CAP_MS = 30_000;
// A pass never hangs (andrewswalesphotography, 18 Oct 2026: an originals pass sat at 4,928 / 5,143 for hours on
// one upload that never answered, until a refresh). Three layers:
//   1. every upload request has a stall timeout (content.ts watchStall) and every API call a timeout;
//   2. a watchdog: a running pass with files left that has made NO progress (no bytes, no file finished, no mint
//      answered) for STALL_KICK_MS — and isn't paused, offline, waiting for a sign-in or between retries — aborts
//      its requests in flight so they retry; still nothing after another STALL_KICK_MS, the files it's stuck on
//      are set aside as "stalled" (the worker holding each is released, its late answer ignored);
//   3. the automatic retry: files that failed for a reason that can pass (a dropped or stalled connection, no
//      upload slot) go again after RETRY_DELAYS_MS — RE-MINTED, so the server answers "already there" for any
//      that landed meanwhile — before they're reported as failed. So every file ends uploaded, already there,
//      or failed with its reason, and the pass finishes.
const STALL_KICK_MS = 3 * 60_000;
const WATCHDOG_TICK_MS = 15_000;
const RETRY_DELAYS_MS = [15_000, 60_000, 180_000];
// A preview-first COPY pass hands its originals on when its copies are done, so it retries sooner and fewer times.
const COPY_RETRY_DELAYS_MS = [5_000, 30_000];
const timing = { stallKickMs: STALL_KICK_MS, watchdogTickMs: WATCHDOG_TICK_MS, retryDelaysMs: RETRY_DELAYS_MS, copyRetryDelaysMs: COPY_RETRY_DELAYS_MS };
/** Test hook: shorten the watchdog / automatic-retry timings (no argument restores them). */
export const setUploadTimingsForTests = (t: Partial<typeof timing> = {}) => {
  timing.stallKickMs = t.stallKickMs ?? STALL_KICK_MS;
  timing.watchdogTickMs = t.watchdogTickMs ?? WATCHDOG_TICK_MS;
  timing.retryDelaysMs = t.retryDelaysMs ?? RETRY_DELAYS_MS;
  timing.copyRetryDelaysMs = t.copyRetryDelaysMs ?? COPY_RETRY_DELAYS_MS;
};

// How often to re-check for a fresh sign-in while uploads wait on one (also re-checked
// immediately on focus / tab switch / another tab writing the session to storage).
const SIGNIN_PROBE_MS = 5000;
// Refreshes after a batch ends, spanning the server's async ingest (~5–15s) so the last files show.
const SETTLE_REFRESH_MS = [5_000, 12_000, 20_000];
const perfNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
const scopeKeyOf = (s: TenantScope) => s.alias ?? s.customDomain ?? "current";

// A job is still "running" (pausable) while it hasn't reached a terminal phase.
// Paused jobs keep their underlying phase, so they qualify too.
const isRunning = (j: UploadJob) =>
  j.phase === "presigning" || j.phase === "uploading" || j.phase === "scanning";

/** An upload worker's place in a batch's pool (see uploadWorker / the stall watchdog). */
interface WorkerSlot { alive: boolean }

let jobCounter = 0;
const nextId = () => `job-${Date.now()}-${jobCounter++}`;
// This browser / OS, sent with each upload-session report (no names: the user agent, cores, memory).
const CLIENT = typeof navigator !== "undefined" ? clientInfo() : undefined;
// Monotonic ordering keys: normal batches append (increasing), prioritised
// batches lead (decreasing negative).
let normalSeq = 0;
let prioSeq = 0;

export const UploadManagerProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  // Ref mirror so async workers mutate without stale closures; state is a throttled view.
  const jobsRef = useRef<Map<string, UploadJob>>(new Map());
  // Live pause/cancel control per job, plus its in-flight AbortControllers. Kept in
  // a ref so the pool reads current flags and cancel can abort in-flight requests.
  const controlsRef = useRef<Map<string, JobControl>>(new Map());
  const flushTimer = useRef<number | null>(null);
  // What each job was started with (the File objects are handles, not bytes — cheap to
  // keep) so "Retry failed" can re-enqueue exactly the files that failed.
  const jobInputs = useRef<Map<string, { args: EnqueueArgs; files: File[]; paths: string[] }>>(new Map());
  // Preview-first sold polling: each running pass registers what it does with newly sold photos, and the
  // originals each pipeline (keyed by the copy pass's id) already sent ahead of the queue.
  const soldHandlers = useRef<Map<string, SoldHandler>>(new Map());
  const sentOriginals = useRef<Map<string, Set<string>>>(new Map());
  // Sold keys a pipeline already sent a same-name file to (the sold photo was uploaded under another path).
  const sentByName = useRef<Map<string, Set<string>>>(new Map());
  const [soldWaiting, setSoldWaiting] = useState<Record<string, SoldWaiting>>({});
  const [soldTick, setSoldTick] = useState(0);
  // `enqueue` for code defined before it (runJob's sold-first batches, the copy → original chain).
  const enqueueRef = useRef<(args: EnqueueArgs) => void>(() => {});
  // Per job: resolves when its CONTENT uploads have drained (before any on-device scanning still
  // running finishes). A preview-first copy pass hands its originals off on this, not on job end.
  const contentDoneRef = useRef<Map<string, () => void>>(new Map());
  // Crop-back tracks, one per workspace: they outlive the copy pass (detection lags the upload, and the
  // originals pass follows), polling in the background until the server is done with each copy's crops.
  const cropBackTracks = useRef<Map<string, CropBackTrack>>(new Map());
  const cropBackTrackFor = useCallback((scope: TenantScope) => {
    const k = scopeKeyOf(scope);
    let t = cropBackTracks.current.get(k);
    if (!t) {
      t = new CropBackTrack({
        poll: (engineGuid, guids, done) => pollCropBack(scope, engineGuid, guids, done, uploadWorkerWanted()),
        cut: cutCropsFromOriginal,
        put: (url, blob) => putArtifact(url, blob, "image/jpeg"),
        putBundle: (target, entries) => uploadBundle(target, entries),
        log: scanLog,
      });
      cropBackTracks.current.set(k, t);
    }
    return t;
  }, []);

  const flush = useCallback(() => {
    // A flush drives a React re-render of the whole uploader tree. If the cancel freeze is a
    // re-render storm, this mark escalates into the thousands right before the console goes silent.
    diagMark("flush");
    setJobs(Array.from(jobsRef.current.values()).sort((a, b) => b.startedAt - a.startedAt));
  }, []);

  // Throttle progress-driven flushes to ~6/s; discrete events flush immediately.
  const flushThrottled = useCallback(() => {
    if (flushTimer.current != null) return;
    flushTimer.current = window.setTimeout(() => {
      flushTimer.current = null;
      flush();
    }, 160);
  }, [flush]);

  const patch = useCallback((id: string, fn: (j: UploadJob) => void, immediate = false) => {
    const job = jobsRef.current.get(id);
    if (!job) return;
    fn(job);
    if (immediate) flush();
    else flushThrottled();
  }, [flush, flushThrottled]);

  // Free the scan workers' memory (each holds an onnxruntime WASM heap plus any
  // bitmap it's mid-decode on) once NO batch is still running. Called when a job
  // finishes OR is cancelled, so cancelling a large run drops hundreds of MB of
  // scan memory promptly instead of leaving the pool resident and grinding — the
  // pressure that froze the browser when cancelling a big batch. Guarded on "no
  // other job running" so a second gallery's upload keeps its pool. The pool
  // rebuilds lazily on the next scan.
  // ---- Expired sign-in: hold, don't fail --------------------------------------------
  // A mint that still gets a 401 after photohawkApi's forced token refresh means the
  // refresh token itself is gone (expired / revoked / signed out elsewhere). Every job
  // that hits it parks here; one probe loop watches for a fresh session (the user signs
  // in again in another tab — Amplify's tokens live in shared localStorage) and releases
  // them all to retry the exact mint that failed.
  const [sessionExpired, setSessionExpired] = useState(false);
  const signInWaiters = useRef(new Set<() => void>());
  const stopProbe = useRef<(() => void) | null>(null);

  const startSignInProbe = useCallback(() => {
    if (stopProbe.current) return;
    let inFlight = false;
    // The id token the failing mint used (if Amplify still holds it). A fresh sign-in is a
    // DIFFERENT token; seeing the same one again must not count, or a token the API keeps
    // rejecting would spin mint → 401 → "signed in" → mint.
    let deadToken: string | null | undefined;
    const check = async () => {
      if (inFlight || !stopProbe.current) return;
      inFlight = true;
      try {
        // Read what's stored — never force a refresh here. A forced refresh with the dead
        // refresh token fails, and Amplify then clears the stored tokens; racing the other
        // tab's sign-in, that could wipe the brand-new session it just wrote.
        const s = await cognitoGetSession(false);
        const tok = s?.tokens?.idToken?.toString() ?? null;
        if (deadToken === undefined) {
          deadToken = tok;
          return;
        }
        if (tok && tok !== deadToken && stopProbe.current) {
          stopProbe.current();
          setSessionExpired(false);
          for (const w of [...signInWaiters.current]) w();
        }
      } finally {
        inFlight = false;
      }
    };
    const onEvent = () => void check();
    const iv = window.setInterval(onEvent, SIGNIN_PROBE_MS);
    window.addEventListener("storage", onEvent);
    window.addEventListener("focus", onEvent);
    window.addEventListener("online", onEvent);
    document.addEventListener("visibilitychange", onEvent);
    stopProbe.current = () => {
      stopProbe.current = null;
      window.clearInterval(iv);
      window.removeEventListener("storage", onEvent);
      window.removeEventListener("focus", onEvent);
      window.removeEventListener("online", onEvent);
      document.removeEventListener("visibilitychange", onEvent);
    };
    void check(); // first pass just records the dead token
  }, []);

  /** Park a job until the user signs in again. Resolves true to retry, false if the job
   *  was cancelled meanwhile. */
  const waitForSignIn = useCallback(
    (jobId: string, control: JobControl): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        let settled = false;
        const finish = (ok: boolean) => {
          if (settled) return;
          settled = true;
          signInWaiters.current.delete(onSignedIn);
          patch(jobId, (j) => { j.waitingForSignIn = false; }, true);
          if (!signInWaiters.current.size && stopProbe.current && !ok) {
            // Last waiter gone (cancelled) — stop probing and clear the prompt.
            stopProbe.current();
            setSessionExpired(false);
          }
          resolve(ok);
        };
        const onSignedIn = () => finish(true);
        signInWaiters.current.add(onSignedIn);
        patch(jobId, (j) => { j.waitingForSignIn = true; }, true);
        setSessionExpired(true);
        startSignInProbe();
        void (async () => {
          while (!settled && !control.cancelled) await control.sig.wait();
          if (control.cancelled) finish(false);
        })();
      }),
    [patch, startSignInProbe],
  );

  const signInAgain = useCallback(() => {
    // reauth=1 makes the login page drop the dead session first instead of bouncing
    // straight through on the stale "signed in" state; /admin/signed-in just says
    // "you can close this tab". Uploads here resume on their own once it succeeds.
    const after = encodeURIComponent("/admin/signed-in");
    window.open(`/admin/login?reauth=1&after=${after}`, "_blank", "noopener");
  }, []);

  useEffect(() => () => stopProbe.current?.(), []);

  // Upload memory diagnostics (gated: localStorage PHX_UPLOAD_DIAG=1, then reload). Attributes a
  // cancel-time heap wedge to a holder. The `manager` probe reports the retained ORIGINAL file bytes —
  // jobInputs keeps every job's File[] for retry and is NOT cleared on cancel, so if those Files are
  // in-memory and the heap tracks this number (and stays up after cancel), they are the wedge.
  useEffect(
    () =>
      registerUploadProbe("manager", () => {
        let files = 0;
        let bytes = 0;
        for (const inp of jobInputs.current.values()) {
          for (const f of inp.files) {
            files += 1;
            bytes += f.size || 0;
          }
        }
        let inflight = 0;
        for (const c of controlsRef.current.values()) inflight += c.controllers.size;
        return { items: files, bytes, note: `jobs=${jobsRef.current.size} inflight=${inflight}` };
      }),
    [],
  );

  // Probe the react-query content cache. `liveRefreshContent` re-fetches the folder listing while an
  // upload runs, and the "content-all" family pages the WHOLE folder — which grows with every landed
  // file. If that retained result is what climbs, this shows it: `queries` is how many content-family
  // cache entries are live, and `items` is the total number of content hits held across them (the
  // objects that back the remote pane's rows + thumbnails). A steady climb in `items` while the UI is
  // windowed means the cache is holding an ever-larger folder snapshot, not that the DOM is unbounded.
  useEffect(
    () =>
      registerUploadProbe("queryCache", () => {
        let queries = 0;
        let items = 0;
        try {
          for (const q of queryClient.getQueryCache().getAll()) {
            const k0 = Array.isArray(q.queryKey) ? q.queryKey[0] : undefined;
            if (k0 !== "content" && k0 !== "content-all" && k0 !== "videos") continue;
            queries += 1;
            const data = q.state.data as unknown;
            // Both families expose their rows as `.hits` (paged) or as a flat array; count whichever.
            if (Array.isArray(data)) items += data.length;
            else if (data && typeof data === "object") {
              const hits = (data as { hits?: unknown }).hits;
              if (Array.isArray(hits)) items += hits.length;
            }
          }
        } catch {
          /* cache shape changed — never let the probe throw */
        }
        return { items, note: `contentQueries=${queries}` };
      }),
    [queryClient],
  );

  // No batch still running → tear down BOTH worker pools (scan + resize), freeing every worker's
  // memory: the scan pool's onnxruntime WASM heaps and the resize pool's in-flight decoded
  // bitmaps/canvases. Both rebuild lazily on the next batch. Called when a job finishes or is
  // cancelled — cancelling a large preview-first batch otherwise left the pools resident and
  // grinding while the tab was already under memory pressure.
  const releaseScanPoolIfIdle = useCallback(() => {
    for (const j of jobsRef.current.values()) {
      if (j.phase === "presigning" || j.phase === "uploading" || j.phase === "scanning") return;
    }
    shutdownScanPool();
    shutdownResizePool();
  }, []);

  // A batch that ended with failures says why, once: "8,925 failed: Safari couldn't read the files (NotReadableError)",
  // the other reasons, what to do, and a button that copies the details for support (no file names).
  const notifyFailures = useCallback((id: string, cancelled: boolean) => {
    const j = jobsRef.current.get(id);
    if (!j || cancelled || j.failed <= 0) return;
    const top = topReasons(j.failReasons)[0]?.[0] ?? "";
    const { title, more } = failureSummary(j.failed, j.failReasons, CLIENT?.browser);
    const hint = top.startsWith("read:") || top.startsWith("not-tried:")
      ? UNREADABLE_NOTICE.description
      : top.startsWith("copy:")
        ? "Retry them, or upload them without previews first."
        : "Retry the failed photos. If they fail again, copy the details and send them to support.";
    // Replaces the batch's earlier notice of the same kind, so one notice is left saying it all.
    const id_ = top.startsWith("read:") || top.startsWith("not-tried:") ? `upload-unreadable-${id}` : `upload-failed-${id}`;
    if (top.startsWith("copy:")) sonnerToast.dismiss(`upload-copy-failed-${id}`);
    sonnerToast.error(title, {
      id: id_,
      description: [...more.map((m) => `Also ${m}.`), hint].join(" "),
      duration: 30000,
      action: { label: "Copy details", onClick: () => void copyUploadDiagnostics(Array.from(jobsRef.current.values())) },
    });
  }, []);

  const runJob = useCallback(
    async (job: UploadJob, scope: TenantScope, files: File[], paths: string[]) => {
      // Control block (pause/cancel flags + in-flight AbortControllers) for this job.
      const control = controlsRef.current.get(job.id) ?? {
        paused: false,
        cancelled: false,
        controllers: new Set<AbortController>(),
        sig: new Signal(),
      };
      controlsRef.current.set(job.id, control);
      // Files from Dropbox / Google Drive (stand-ins; the server copies the real file).
      const cloudSrc = files.map((f) => cloudSourceOf(f));
      const cloudJob = cloudSrc.some(Boolean);
      // Upload Worker for resized copies only (preview-first copies, resize presets): many small files,
      // where HTTP/2 through Cloudflare's edge got previews up 15–24% faster on staging. Full-size
      // originals go direct: 6 HTTP/1.1 connections moved big files faster than one HTTP/2 connection
      // on a fast link, and matched it on slower ones.
      const viaWorker = !cloudJob && !!job.resize;
      // Preview-phase speed-ups (on unless localStorage ph_preview_speed=0): per-file completions re-render
      // on the throttled flush, and small files over the Worker start at SMALL_CONC_START_WORKER uploads in
      // flight and may climb to SMALL_CONC_MAX.
      const fastPreviews = previewSpeedOn();
      let reconnectShown = false;
      // Wait out a pause: the pool holds here (claims no new files) until resumed
      // or cancelled. Resolves immediately when not paused. Event-driven (woken by
      // resume/cancel), so a resume in a background tab isn't delayed by timer throttling.
      const holdWhilePaused = async () => {
        while (control.paused && !control.cancelled) await control.sig.wait();
      };
      // Why the last mint failed (shown on the failed rows, so a person — and support — can see why).
      let lastMintError = "";
      let lastMintName = "unknown";
      // Count a failed file's reason on the batch (and its row). Call inside a patch.
      const noteFail = (j: UploadJob, i: number | null, key: FailKey, n = 1) => {
        j.failReasons = countFail(j.failReasons, key, n);
        if (i != null) { const fp = j.files[i]; if (fp) fp.reason = key; }
      };
      // Mint with retry — see MINT_ATTEMPTS. Returns the mint, null for a permanent
      // failure (the chunk's files are counted failed), or "cancelled".
      const mintWithRetry = async (
        chunkFiles: File[],
        chunkPaths: string[],
        useScan: boolean,
        allowMultipart: boolean,
      ): Promise<{ urls: MintUrl[]; prescanAvailable: boolean; cropBack?: boolean; bundle?: BundleTarget | null } | null | "cancelled"> => {
        let attempt = 0;
        for (;;) {
          if (control.cancelled) return "cancelled";
          if (control.blocked || control.previewFirstRefused) return null; // plan limit — already explained to the user
          await waitOnline(control.sig); // offline → wait for the network, no attempt spent
          if (control.cancelled) return "cancelled";
          const ac = new AbortController();
          control.controllers.add(ac);
          try {
            return await presignChunk(
              scope, job.engineGuid, chunkFiles, chunkPaths, job.folder, useScan, ac.signal, allowMultipart,
              job.previewFirst, viaWorker,
            );
          } catch (err) {
            if (control.cancelled) return "cancelled";
            lastMintError = err instanceof PhotohawkApiError ? `${err.status || "network"}: ${err.message}` : err instanceof Error ? err.message : String(err);
            lastMintName = mintErrorName(err instanceof PhotohawkApiError ? err.status : 0, err);
            // Plan limits (402 STORAGE_CARD_REQUIRED / 403 PLAN_FEATURE): not retryable and not a
            // sign-in problem — say why once and stop minting this batch. Nothing already uploaded
            // is affected.
            const limit = planLimitOf(err);
            // Previews first isn't on this workspace's plan: not an error — the mint pump hands this
            // chunk (and every later one) to an ordinary upload.
            if (limit && limit.code === "PLAN_FEATURE" && limit.feature === "previewFirst" && job.previewFirst === "copy") {
              if (!control.previewFirstRefused) {
                control.previewFirstRefused = true;
                sonnerToast.info("Uploading the originals instead", {
                  id: "upload-preview-first-plan",
                  description: limit.message,
                  duration: 10000,
                });
              }
              return null;
            }
            if (limit && (limit.code === "STORAGE_CARD_REQUIRED" || limit.code === "PLAN_FEATURE")) {
              if (!control.blocked) {
                control.blocked = limit.message;
                sonnerToast.error(limit.code === "STORAGE_CARD_REQUIRED" ? "Add a card to keep uploading" : "Not included in your plan", {
                  id: `upload-plan-limit-${limit.code}`,
                  description: limit.message,
                  duration: 15000,
                });
              }
              return null;
            }
            // A refused mint can also be a dead sign-in the backend didn't report as 401 (older
            // builds answered an unauthenticated mint with a bare 400) — so before failing 75
            // photos, check the session itself: no token after a forced refresh = sign in again.
            const refused = err instanceof PhotohawkApiError && (err.status === 400 || err.status === 403);
            if (isSessionExpired(err) || (refused && !(await sessionAlive()))) {
              if (!(await waitForSignIn(job.id, control))) return "cancelled";
              continue; // same chunk again with the fresh session; no attempt spent
            }
            if (typeof navigator !== "undefined" && navigator.onLine === false) continue;
            attempt += 1;
            if (!isTransientApiError(err) || attempt >= MINT_ATTEMPTS) return null;
            await sleep(
              Math.min(MINT_BACKOFF_CAP_MS, MINT_BACKOFF_MS * 2 ** (attempt - 1)) + Math.floor(Math.random() * 500),
            );
          } finally {
            control.controllers.delete(ac);
          }
        }
      };
      // Best-effort progress reporting → DynamoDB (via /admin/upload-sessions) so
      // owners/admins can see active uploads on the dashboard + gallery overview.
      // Identity is stamped server-side from the token; never blocks or fails an upload.
      // Throttled "live" refresh of the content lists WHILE a batch uploads, so views
      // show files as they land rather than only at batch end. Content is ingested
      // asynchronously after each PUT, so refetching on every file would mostly race
      // the ingest and hammer the API — coalesce to at most once per LIVE_REFRESH_MS,
      // leading AND trailing (a burst that ends inside the window still gets its
      // refresh). Invalidates BOTH content query families (see the batch-end refresh
      // for why): paged ["content", ...] and the remote pane's ["content-all", ...].
      // (The uploader's remote pane also polls on its own while the gallery is hot.)
      let lastLiveRefreshAt = 0;
      let trailingTimer: number | null = null;
      const LIVE_REFRESH_MS = 8000;
      const invalidateContent = () => {
        lastLiveRefreshAt = Date.now();
        queryClient.invalidateQueries({ queryKey: ["content", job.scopeKey, job.engineGuid] });
        queryClient.invalidateQueries({ queryKey: ["content-all", job.scopeKey, job.engineGuid] });
      };
      const liveRefreshContent = () => {
        const wait = LIVE_REFRESH_MS - (Date.now() - lastLiveRefreshAt);
        if (wait <= 0) { invalidateContent(); return; }
        if (trailingTimer == null) {
          trailingTimer = window.setTimeout(() => { trailingTimer = null; invalidateContent(); }, wait);
        }
      };
      const reportSession = () => {
        const j = jobsRef.current.get(job.id);
        if (!j) return;
        void reportUploadSession(scope, {
          sessionId: job.id,
          engineGuid: job.engineGuid,
          galleryName: job.galleryName,
          folderGuid: job.folder,
          kind: job.kind,
          total: j.total,
          completed: j.completed,
          failed: j.failed,
          bytesTotal: j.bytesTotal,
          bytesSent: j.bytesSent,
          scanTotal: j.scanTotal,
          scanCompleted: j.scanCompleted,
          phase: j.phase,
          // Why files failed (counts per reason, no names) and where: support reads these with diag-upload-attempts.
          failReasons: j.failReasons,
          pass: job.previewFirst ?? (job.resize ? "resize" : "original"),
          client: CLIENT,
        });
      };
      // Report on start, then a throttled heartbeat while running; the terminal state
      // is reported once from the finally block below.
      reportSession();
      const reportTimer = window.setInterval(reportSession, 5000);
      let stopWatchdog = () => {};
      try {
        // Track per-file last-sent bytes so we can accumulate an accurate total.
        const sent = new Array(files.length).fill(0);

        // On-device scanning gate: images only, gallery scan-enabled, and this
        // device is capable. `serverPrescan` latches off if the backend declines
        // to mint prescan targets. Any of these false → plain upload, server scans.
        let scanOn = job.kind === "image" && !!job.scan?.enabled;
        const scanPolicyOn = scanOn;
        if (scanOn) scanOn = await probeScan();
        let serverPrescan = scanOn;
        const wantPeople = job.scan?.needsPeople !== false;
        const wantFaces = job.scan?.needsFaces !== false;
        scanLog(
          `batch ${job.id} (${job.galleryName ?? job.engineGuid}): on-device scan`,
          scanOn ? "ENABLED" : "disabled",
          "—",
          {
            kind: job.kind,
            scanPolicyEnabled: scanPolicyOn,
            probeViable: scanPolicyOn ? scanOn : "n/a (policy off)",
            wantFaces,
            wantPeople,
            files: files.length,
          },
          scanOn ? "" : "→ files will be server-scanned",
        );
        // Surface on-device scan progress (content upload finishes well before this).
        let scanCompleted = 0;
        if (scanOn) patch(job.id, (j) => {
          j.scanTotal = files.length;
          j.scanCompleted = 0;
          // Mark every file "pending" scan so the per-file table can show a
          // scan/sidecar column from the outset (only when the batch scans).
          for (const fp of j.files) fp.scan = "pending";
        }, true);

        let uploadingMarked = false;

        // ---- Decoupled pipeline: content upload and scan run fully independently ----
        // The content upload NEVER waits for scanning, so throughput tracks the user's
        // bandwidth; the scan stream works through EVERY file at its own (CPU-bound) pace
        // and uploads crops+sidecars off the critical path, so on-device coverage still
        // reaches ~100%. The backend holds server-side scanning (deferred prescan) until
        // each sidecar lands or a deadline passes, so nothing is double-scanned or stuck.
        //   mint pump    : mints DEFERRED content URLs a MODEST distance ahead of the
        //                  uploader (MINT_LOOKAHEAD) and records each file's contentGuid.
        //   upload workers: CONCURRENCY of them; each awaits only its own presignGate then
        //                  PUTs the content — bandwidth-bound, nothing else.
        //   scan pump    : scanCoroutineCount() coroutines scan every file on-device (one
        //                  per scan worker, plus headroom for the network gap), then (once
        //                  that file has minted, so its contentGuid is known) request its
        //                  crop/sidecar targets and upload them. Runs through ALL files,
        //                  independent of the upload frontier.
        const n = files.length;

        // ---- Every file reaches exactly one terminal state ----------------------------------------------
        // settled[i]: file i is done (uploaded, already there, handed on) or failed — counted ONCE. gen[i] moves
        // on when the watchdog gives up on the worker holding i, so that worker's late answer is ignored. The pass
        // can finish once every file is settled, even if a request somewhere never answered (whenAllSettled).
        const settled = new Uint8Array(n);
        const gen = new Uint32Array(n);
        let settledCount = 0;
        let settledWaiters: Array<() => void> = [];
        const settle = (i: number, g?: number): boolean => {
          if (settled[i] || (g !== undefined && gen[i] !== g)) return false;
          settled[i] = 1;
          settledCount += 1;
          if (settledCount >= n && settledWaiters.length) {
            const ws = settledWaiters;
            settledWaiters = [];
            for (const w of ws) w();
          }
          return true;
        };
        const unsettle = (i: number) => {
          if (!settled[i]) return;
          settled[i] = 0;
          settledCount -= 1;
        };
        const whenAllSettled = () =>
          settledCount >= n ? Promise.resolve() : new Promise<void>((r) => settledWaiters.push(r));
        // Files an upload worker holds right now, with the generation it holds and its worker (for the watchdog).
        const inflight = new Map<number, { g: number; w: WorkerSlot }>();

        // Presigned POST per file (null = mint failed → already counted failed) plus its
        // server contentGuid, gated so an upload worker waits until its URL is minted.
        const posts: (MintUrl | null)[] = new Array(n).fill(null);
        const contentGuids: (string | null)[] = new Array(n).fill(null);
        // Copies whose mint said cropBack: once uploaded, the crop-back track cuts their crops.
        const cropBackOn: boolean[] = new Array(n).fill(false);
        // Bundled uploads (Worker POST /b, flag ph_up_bundle=1): the endpoint from the mint, and the files
        // a bundle didn't get acked — re-sent one by one on the normal single-file path.
        let bundleTarget: BundleTarget | null = null;
        let bundleFails = 0;
        const retry: number[] = [];
        const presignDone: boolean[] = new Array(n).fill(false);
        const presignResolvers: Array<() => void> = new Array(n);
        const presignGates: Array<Promise<void>> = files.map(
          (_, i) =>
            new Promise<void>((resolve) => {
              presignResolvers[i] = () => {
                if (!presignDone[i]) {
                  presignDone[i] = true;
                  resolve();
                }
              };
            }),
        );

        let uploadCursor = 0; // upload frontier — the mint pump paces against this
        let mintIdx = 0;      // next file the mint pump will mint
        let scanIdx = 0;      // next file the scan pump will scan (runs through ALL files)

        // Just-in-time resize: downscale/re-encode each image the moment a consumer
        // reaches it — never waiting for the whole batch to resize first. Bounded to
        // RESIZE_CONC concurrent resizes (matches the batch resizer) so a huge batch
        // never floods the resize worker.
        //
        // Memory: the content upload and the on-device scan each make their OWN
        // short-lived resized copy from the on-disk original and drop it the instant
        // they're done with that file (it's just a local, GC'd on the next iteration).
        // So a 100k batch never holds more than ~(upload + scan concurrency) resized
        // copies at once (~tens of MB) instead of retaining every ~400 KB copy to the
        // end of the run (~tens of GB, which crashed the tab). The two copies come from
        // the SAME deterministic downscale (identical dimensions), so detection boxes
        // still map to the stored image. When resize is off, both consumers get the
        // original file straight from disk — nothing is held in memory at all.
        const doResize = job.kind === "image" && !!job.resize;
        const resizeOpts = job.resize;
        const RESIZE_CONC = RESIZE_CONCURRENCY; // the pool's workers × resizes in flight per worker
        let resizeActive = 0;
        const resizeWaiters: Array<() => void> = [];
        const acquireResize = async () => {
          if (resizeActive >= RESIZE_CONC) await new Promise<void>((r) => resizeWaiters.push(r));
          resizeActive += 1;
        };
        const releaseResize = () => {
          resizeActive -= 1;
          resizeWaiters.shift()?.();
        };
        // A fresh resized copy of file i (or the original when resize is off / on
        // failure). Not memoised — each caller owns and releases its own copy.
        const resizeOne = async (i: number): Promise<File> => {
          if (!doResize || !resizeOpts) return files[i];
          // A preview-first copy is only its chosen size (default 1200px) when its mint accepted crop-back (the crops then come
          // from the original); otherwise it stays 2048px. Resizes run after the file's mint.
          // (The chosen quality still applies to the 2048px copy.)
          const opts = job.previewFirst === "copy" && !cropBackOn[i] ? { ...PREVIEW_COPY_RESIZE_NO_CROP_BACK, quality: resizeOpts.quality ?? PREVIEW_QUALITY } : resizeOpts;
          // An unreadable original (see failUnreadable) costs no resize. The upload worker checks before it asks for
          // the copy, so this only spares the resize-ahead / scan tracks the work.
          const rerr = await readError(files[i]);
          if (rerr) {
            if (job.previewFirst === "copy") throw new PreviewCopyError(`the file can't be read (${rerr})`, rerr);
            return files[i];
          }
          await acquireResize();
          try {
            // A preview-first copy that can't be made FAILS (PreviewCopyError) — the original never goes up in
            // its place. A resize preset keeps the original instead (it's what the person would get anyway).
            if (job.previewFirst === "copy") return await resizeForCopy(files[i], opts);
            return await resizeToJpeg(files[i], opts);
          } catch (err) {
            if (err instanceof PreviewCopyError) throw err;
            return files[i]; // never lose a file — fall back to the original
          } finally {
            releaseResize();
          }
        };
        // Count file i failed (call inside a patch, after settle(i) said yes): its row, its reason, and — where the
        // bytes are the file's own (no resize) — its bytes leave the total, so "time left" isn't counting photos
        // that aren't being sent. The automatic retry puts them back (unfailRow).
        const failRow = (j: UploadJob, i: number, key: FailKey, error?: string) => {
          j.failed += 1;
          const fp = j.files[i];
          if (fp) {
            fp.status = "error";
            if (error) fp.error = error;
            if (!doResize) j.bytesTotal -= fp.size;
          }
          noteFail(j, i, key);
        };
        const unfailRow = (j: UploadJob, i: number) => {
          const fp = j.files[i];
          if (!fp || fp.status !== "error") return;
          j.failed -= 1;
          if (fp.reason && j.failReasons?.[fp.reason]) {
            j.failReasons[fp.reason] -= 1;
            if (j.failReasons[fp.reason] <= 0) delete j.failReasons[fp.reason];
          }
          if (!doResize) j.bytesTotal += fp.size;
          fp.status = "queued";
          fp.error = undefined;
          fp.reason = undefined;
          fp.loaded = 0;
        };
        // Fail several files at once (a mint that gave no slots) — only those not already settled.
        const failMany = (idx: number[], key: FailKey, error?: string) => {
          const now = idx.filter((i) => settle(i));
          if (!now.length) return;
          patch(job.id, (j) => { for (const i of now) failRow(j, i, key, error); }, true);
        };
        // A copy that couldn't be made: that one photo fails, with the reason on its row (and one notice).
        let copyFailNoticed = false;
        const failCopy = (i: number, err: PreviewCopyError, g?: number) => {
          if (!settle(i, g)) return;
          patch(job.id, (j) => failRow(j, i, failKey("copy", err.causeName), `${err.message} (${err.detail})`), true);
          posts[i] = null;
          if (!copyFailNoticed) {
            copyFailNoticed = true;
            console.warn(`[upload] preview copy failed for ${files[i]?.name}: ${err.detail}`);
            sonnerToast.error("Some previews couldn't be made", {
              id: `upload-copy-failed-${job.id}`,
              description: "This browser couldn't make a quick preview of some photos, so they weren't uploaded. Retry them, or upload them without previews first.",
              duration: 12000,
            });
          }
        };
        // A file the browser can no longer read (changed / moved since it was added, or online-only in iCloud
        // Drive / Dropbox): it fails with that reason, and one notice says what to do. Checked BEFORE the resize,
        // so it isn't mistaken for "this browser can't make previews". A batch that has read nothing after
        // UNREADABLE_STOP_AFTER such files stops: the rest wouldn't read either, and minting them is wasted.
        let unreadableCount = 0;
        let unreadableStop: string | null = null;
        const failUnreadable = (i: number, name: string, g?: number) => {
          if (!settle(i, g)) return;
          unreadableCount += 1;
          const j0 = jobsRef.current.get(job.id);
          const stopNow = !unreadableStop && unreadableCount >= UNREADABLE_STOP_AFTER && (j0?.completed ?? 0) === 0;
          if (stopNow) unreadableStop = name;
          patch(job.id, (j) => failRow(j, i, failKey("read", name), unreadableReason(name)), true);
          posts[i] = null;
          if (unreadableCount === 1 || stopNow) {
            console.warn(`[upload] ${files[i]?.name} can't be read (${name})${stopNow ? " — stopping this batch" : ""}`);
            sonnerToast.error(UNREADABLE_NOTICE.title, {
              id: `upload-unreadable-${job.id}`,
              description: stopNow ? `${UNREADABLE_NOTICE.description} ${UNREADABLE_NOTICE.stopped}` : UNREADABLE_NOTICE.description,
              duration: 20000,
            });
          }
          if (stopNow) control.sig.notify(); // wake the mint pump so it fails the rest without minting
        };
        // Shared resized-copy cache. The upload track and the scan track both need the SAME
        // deterministic downscale of file i (byte-identical opts), and today each computes its
        // own — resizing/decoding/re-encoding every scanned photo TWICE. Memoise the resize
        // PROMISE per index so whichever track reaches i first computes it and the other reuses
        // the same File by reference (both only read it → byte-identical, so detection parity and
        // the stored image are unchanged). Hard-capped LRU: leak-proof and memory-bounded on
        // 50k-file batches (a trailing consumer that falls outside the window simply recomputes,
        // exactly as before). Also halves pressure on the RESIZE_CONC gate.
        const SHARE_CAP = 32;
        const resizeShare = new Map<number, Promise<File>>(); // insertion-ordered → oldest-first
        const sharedResize = (i: number): Promise<File> => {
          let p = resizeShare.get(i);
          if (!p) {
            p = resizeOne(i);
            p.catch(() => {}); // a failed copy is handled by whoever awaits it (resize-ahead doesn't)
            resizeShare.set(i, p);
            if (resizeShare.size > SHARE_CAP) {
              const oldest = resizeShare.keys().next().value;
              if (oldest !== undefined) resizeShare.delete(oldest);
            }
          }
          return p;
        };
        // The UPLOAD's copy additionally corrects the job byte totals (seeded from the
        // large originals at enqueue) down to the shrunk size, ONCE per file, so the bar
        // and the file row still reach 100%. The scan's copy does no byte accounting.
        //
        // Files not resized yet would otherwise still count at their ORIGINAL size — ~15×
        // too big for a 1200px copy — so the % would crawl and the ETA read hours until the
        // very end. Instead they count at a PROJECTED size: the shrink ratio measured on the
        // files resized so far (a size-based guess before the first one lands).
        const est = { pendingOrig: 0, doneOrig: 0, doneOut: 0, adj: 0 };
        const projectRatio = () => {
          if (est.doneOrig > 0) return est.doneOut / est.doneOrig;
          if (!resizeOpts) return 1;
          const q = resizeOpts.quality ?? 0.85;
          const perFile = estimatedCopyBytes(resizeOpts.maxEdge, q);
          const avg = files.length ? files.reduce((a, f) => a + f.size, 0) / files.length : perFile;
          return Math.min(1, perFile / Math.max(1, avg));
        };
        const reproject = () => {
          const adj = (projectRatio() - 1) * est.pendingOrig;
          const d = adj - est.adj;
          est.adj = adj;
          return d;
        };
        if (doResize) {
          est.pendingOrig = files.reduce((a, f) => a + f.size, 0);
          const d = reproject();
          if (d) patch(job.id, (j) => { j.bytesTotal += d; });
        }
        // Resize ahead of the upload frontier. Each worker resizes its file and then uploads it, so
        // without this the resize pool sits idle while every worker waits on the network, and a
        // preview-first copy pass can never upload faster than one resize per worker at a time.
        // Starting the next few copies as each file is claimed keeps the pool busy, so a ready copy
        // is waiting when a worker frees up. Only files that already have an upload target (the mint
        // pump runs ahead), so a failed mint never costs a resize. Bounded by the shared cache
        // (SHARE_CAP), so memory stays at a few dozen ~400 KB copies.
        const RESIZE_AHEAD = Math.min(SHARE_CAP / 2, RESIZE_CONC * 2);
        const resizeAhead = (from: number) => {
          if (!doResize || unreadableStop) return;
          const last = Math.min(n - 1, from + RESIZE_AHEAD);
          for (let k = from + 1; k <= last; k += 1) {
            const post = posts[k];
            if (presignDone[k] && post && !post.skip && !cloudSrc[k] && !resizeShare.has(k)) void sharedResize(k);
          }
        };
        const projected = new Set<number>();
        const resizeForUpload = async (i: number): Promise<File> => {
          const out = await sharedResize(i);
          if (doResize && !projected.has(i)) {
            projected.add(i);
            const delta = out.size - files[i].size;
            est.pendingOrig -= files[i].size;
            est.doneOrig += files[i].size;
            est.doneOut += out.size;
            const d = reproject();
            if (delta !== 0 || d !== 0) {
              patch(job.id, (j) => {
                j.bytesTotal += delta + d;
                const fp = j.files[i];
                if (fp) fp.size = out.size;
              });
            }
          }
          return out;
        };

        // Adaptive upload pool: `target` is the worker count the controller aims for,
        // `active` is how many are running now. The controller (below) hill-climbs
        // `target` on measured throughput; workers self-retire when over target.
        // Small-file batch: preview copies / resized uploads (not cloud imports, which the server copies).
        const smallFiles = !cloudJob && !!job.resize;
        const smallStart = fastPreviews && viaWorker && uploadWorkerWanted() ? SMALL_CONC_START_WORKER : SMALL_CONC_START;
        let target = Math.min(cloudJob ? CLOUD_CONC : smallFiles ? smallStart : CONC_START, n);
        let active = 0;
        const workers = new Set<Promise<void>>();

        // Mint DEFERRED content URLs ahead of the uploader, recording each contentGuid.
        // `scanOn` is already resolved (the probe was awaited above), so `useScan` is known
        // for the FIRST batch and every file mints deferred from the start — fixing the
        // small-batch zero-coverage case where the scanner hadn't finished before the mint.
        const mintPump = async () => {
          try {
          const useScan = scanOn && serverPrescan;
          // Multipart is video-only (kill switch: localStorage ph_multipart=0), so photo
          // batches and the resize path are entirely untouched by it.
          const allowMultipart = job.kind === "video" && multipartEnabled();
          while (mintIdx < n) {
            if (control.cancelled) return;
            // Stay a modest distance ahead of the uploader — enough to hide the mint
            // round-trip so workers never wait, without minting the whole batch up front.
            // Woken by the uploader claiming a file (or pause/resume/cancel) — never a
            // polling timer, which Chrome throttles to ~1/min in a hidden tab.
            if (mintIdx - uploadCursor >= MINT_LOOKAHEAD && !unreadableStop) {
              await control.sig.wait();
              continue;
            }
            const startIdx = mintIdx;
            const end = Math.min(n, startIdx + MINT_BATCH);
            mintIdx = end;
            const chunk = files.slice(startIdx, end);
            const chunkIdx = Array.from({ length: end - startIdx }, (_, k) => startIdx + k);
            if (unreadableStop) {
              // Nothing in this batch could be read: fail the rest without minting them.
              failMany(chunkIdx, "not-tried:read", NOT_TRIED_UNREADABLE);
              for (let k = startIdx; k < end; k += 1) presignResolvers[k]();
              continue;
            }

            const minted = await mintWithRetry(chunk, paths.slice(startIdx, end), useScan, allowMultipart);
            // Cancelled mid-presign → the request was aborted; stop, don't count failed.
            if (minted === "cancelled") return;
            if (minted === null && control.previewFirstRefused) {
              // Previews first isn't on this workspace's plan: these photos go up as an ordinary upload
              // (a plain batch of the same files, originals only) — shown here as handed on, not failed.
              const idx = Array.from({ length: chunk.length }, (_, k) => startIdx + k);
              for (const i of idx) { handedOff.add(i); posts[i] = { skip: true } as MintUrl; }
              const args = jobInputs.current.get(job.id)?.args;
              if (args && !control.cancelled) {
                enqueueRef.current({
                  ...args,
                  files: idx.map((i) => files[i]),
                  paths: idx.map((i) => paths[i]),
                  previewFirst: undefined,
                  pairId: undefined,
                  prioritise: false,
                  soldFirst: undefined,
                });
              }
              for (const i of idx) presignResolvers[i]();
              continue;
            }
            if (minted === null) {
              const why = `Couldn't get an upload slot from the server${lastMintError ? ` (${lastMintError})` : ""}`;
              failMany(chunkIdx, control.blocked ? failKey("plan", lastMintName) : failKey("mint", lastMintName), why);
              for (let k = 0; k < chunk.length; k += 1) presignResolvers[startIdx + k](); // posts[i] stays null
              continue;
            }
            const { urls, prescanAvailable } = minted;
            if (!urls || urls.length < chunk.length) {
              failMany(chunkIdx, "mint:short", "The server returned fewer upload slots than photos");
              for (let k = 0; k < chunk.length; k += 1) presignResolvers[startIdx + k]();
              continue;
            }
            if (!uploadingMarked) {
              uploadingMarked = true;
              patch(job.id, (j) => { j.phase = "uploading"; j.uploadingAt ??= Date.now(); }, true);
            }
            // Backend can't mint prescan targets → stop the scan stream; content still uploads.
            if (!bundleTarget && minted.bundle && viaWorker && uploadBundleWanted() && uploadWorkerWanted()) bundleTarget = minted.bundle;
            if (useScan && !prescanAvailable) {
              scanLog(
                `batch ${job.id}: backend did not mint prescan targets (prescanAvailable=false)`,
                "— on-device scan OFF, server will scan; content uploads normally",
              );
              scanOn = false;
              serverPrescan = false;
            }
            for (let k = 0; k < chunk.length; k += 1) {
              posts[startIdx + k] = urls[k];
              contentGuids[startIdx + k] = urls[k].contentGuid ?? null;
              cropBackOn[startIdx + k] = minted.cropBack === true;
              presignResolvers[startIdx + k]();
            }
          }
          } finally {
            // ALWAYS release every presign gate once minting ends — normal finish,
            // cancel, or error. Otherwise an upload worker awaiting an un-minted
            // file's gate would hang forever, so the job's Promise.all never settles
            // and its files/blobs are never freed (a cancel then leaks the whole
            // batch — enough to crash the tab). Resolvers are idempotent.
            for (let k = 0; k < n; k += 1) presignResolvers[k]();
          }
        };

        // Preview-first originals: a file the server says is already there (`skip`), or one
        // handed to a prioritised "sold first" batch (below), completes WITHOUT an upload. Its
        // bytes leave the job total instead of counting as sent, so the bar still reaches 100%
        // and the tuner's throughput isn't inflated by bytes that never went over the wire.
        const handedOff = new Set<number>();
        // `already`: the server said its original is already there (not a hand-off to another batch) — counted
        // as done AND as "already on the server", never as a failure.
        const markSkipped = (i: number, g?: number, already = !handedOff.has(i)) => {
          posts[i] = null;
          if (!settle(i, g)) return;
          patch(job.id, (j) => {
            const fp = j.files[i];
            j.completed += 1;
            if (already) j.alreadyThere = (j.alreadyThere ?? 0) + 1;
            if (fp) { j.bytesTotal -= fp.size; fp.loaded = fp.size; fp.status = "done"; fp.skipped = true; }
          }, !fastPreviews); // throttled: a resumed originals pass can skip a whole mint's worth at once
        };

        // A file landed (single PUT or bundle entry): count it, show it, hand a copy to crop-back.
        // Per-file completions use the THROTTLED flush (≤ ~6 renders/s, the same as progress): an
        // immediate flush re-rendered the whole uploader once per landed file — and every consumer walks
        // the batch's file rows — so the page's main thread did O(files) work per photo, which at
        // dozens of previews a second competed with the uploads it was reporting on.
        const finishUploaded = (i: number, file: File, g?: number) => {
          if (!settle(i, g)) { posts[i] = null; return; }
          const remainder = file.size - sent[i];
          patch(job.id, (j) => {
            if (remainder > 0) j.bytesSent += remainder;
            j.completed += 1;
            const fp = j.files[i];
            if (fp) { fp.loaded = fp.size; fp.status = "done"; }
          }, !fastPreviews);
          liveRefreshContent();
          if (cropBackOn[i] && !cloudSrc[i] && job.previewFirst === "copy") {
            cropBackTrackFor(scope).track(job.engineGuid, posts[i]?.contentGuid ?? contentGuids[i], files[i]);
          } else if (job.previewFirst === "original") {
            cropBackTrackFor(scope).untrack(posts[i]?.contentGuid ?? contentGuids[i]);
          }
          posts[i] = null;
        };

        // Bundled Worker upload of the next run of files (≤ BUNDLE_FILES, ≤ the bundle's byte cap). Each
        // entry the Worker acks is done; everything else — not Worker-eligible, over the cap, not acked, or
        // the whole bundle on a network error — goes back to `retry` for the single-file path, which has
        // its own Worker → presigned fallback and retries. Two failed bundles turn bundling off for the job.
        const uploadGroup = async () => {
          const target = bundleTarget!;
          const group: number[] = [];
          const max = Math.min(BUNDLE_FILES, target.maxEntries);
          while (group.length < max && uploadCursor < n) {
            group.push(uploadCursor);
            uploadCursor += 1;
          }
          const gens = new Map(group.map((i) => [i, gen[i]] as const)); // a late answer after a give-up is ignored
          control.sig.notify();
          await Promise.all(group.map((i) => presignGates[i]));
          if (control.cancelled || !group.length) return;
          resizeAhead(group[group.length - 1]);
          const ready: number[] = [];
          for (const i of group) {
            const post = posts[i];
            if (!post) {
              patch(job.id, (j) => { const fp = j.files[i]; if (fp) fp.status = "error"; }, true);
              continue;
            }
            if (post.skip || handedOff.has(i)) { markSkipped(i, gens.get(i)); continue; }
            if (cloudSrc[i] || !post.worker || post.multipart) { retry.push(i); continue; }
            ready.push(i);
          }
          if (!ready.length) return;
          const readErrs = await Promise.all(ready.map((i) => readError(files[i])));
          if (control.cancelled) return;
          ready.forEach((i, k) => { const e = readErrs[k]; if (e) failUnreadable(i, e, gens.get(i)); });
          ready.splice(0, ready.length, ...ready.filter((_, k) => !readErrs[k]));
          if (!ready.length) return;
          const resized = await Promise.allSettled(ready.map((i) => resizeForUpload(i)));
          if (control.cancelled) return;
          const take: number[] = [];
          const blobs: File[] = [];
          let bytes = 4;
          ready.forEach((i, k) => {
            const r = resized[k];
            if (r.status === "rejected") {
              if (r.reason instanceof PreviewCopyError) failCopy(i, r.reason, gens.get(i));
              else retry.push(i);
              return;
            }
            const size = r.value.size + 512; // + its frame header
            if (bytes + size <= target.maxBytes) { take.push(i); blobs.push(r.value); bytes += size; } else retry.push(i);
          });
          for (const i of take) patch(job.id, (j) => { const fp = j.files[i]; if (fp && fp.status === "queued") fp.status = "uploading"; });
          const ac = new AbortController();
          control.controllers.add(ac);
          let acked: boolean[] = take.map(() => false);
          try {
            acked = await uploadBundle(target, take.map((i, k) => ({ token: posts[i]!.worker!.token, blob: blobs[k] })), (now) => {
              now.forEach((v, k) => {
                const i = take[k];
                const d = v - sent[i];
                if (d > 0) {
                  sent[i] = v;
                  patch(job.id, (j) => { j.bytesSent += d; const fp = j.files[i]; if (fp) { fp.loaded = v; fp.status = "uploading"; } });
                }
              });
            }, ac.signal);
            bundleFails = 0;
          } catch {
            if (control.cancelled) return;
            bundleFails += 1;
            if (bundleFails >= 2) bundleTarget = null; // bundles keep failing here — plain uploads from now on
          } finally {
            control.controllers.delete(ac);
          }
          take.forEach((i, k) => {
            if (acked[k]) { finishUploaded(i, blobs[k], gens.get(i)); return; }
            if (sent[i] > 0) {
              const partial = sent[i];
              sent[i] = 0;
              patch(job.id, (j) => { j.bytesSent -= partial; const fp = j.files[i]; if (fp) fp.loaded = 0; });
            }
            retry.push(i);
          });
        };

        // Upload workers claim files in order and PUT the content back-to-back. Each awaits
        // only its own presignGate (already resolved by the mint pump running ahead), so a
        // freed worker immediately starts the next file — never blocked on scanning.
        // `slot`: its place in the pool. The watchdog can give up on a worker stuck on a request that never answers:
        // it frees the place (alive = false, active -= 1) so the pool carries on without it.
        const uploadWorker = async (slot: WorkerSlot) => {
          active += 1;
          try {
          for (;;) {
            if (control.cancelled || !slot.alive) return;
            // Hold (without claiming a new file) while paused. Any file already
            // claimed below is always uploaded — pause is only ever observed BEFORE
            // a claim, so a resumed job never skips or double-sends a file.
            if (control.paused) {
              await holdWhilePaused();
              if (control.cancelled) return;
            }
            // Retire when the controller has lowered the target below the live count.
            if (active > target) return;
            // Bundled uploads: claim a run of files and send them in ONE request (re-sends go single).
            if (bundleTarget && !retry.length && uploadCursor < n) {
              await uploadGroup();
              continue;
            }
            let i: number;
            if (retry.length) {
              i = retry.shift()!;
            } else {
              i = uploadCursor;
              if (i >= n) return;
              uploadCursor += 1;
              control.sig.notify(); // let the mint pump top up its lookahead
            }
            const g = gen[i];
            inflight.set(i, { g, w: slot });
            try {
            await presignGates[i];
            if (control.cancelled) return; // cancelled while awaiting the mint → drop it
            if (gen[i] !== g) continue; // the watchdog gave up on this file meanwhile
            resizeAhead(i); // i's chunk has minted, so the files after it have targets too
            let post = posts[i];
            if (!post) {
              // mint failed → already counted failed; reflect it on the file row.
              patch(job.id, (j) => { const fp = j.files[i]; if (fp) fp.status = "error"; }, true);
              posts[i] = null;
              continue;
            }
            if (post.skip || handedOff.has(i)) { markSkipped(i, g); continue; }
            // Resize just-in-time (no-op when resize is off) — the mint already ran ahead,
            // so the only wait here is this one file's resize, not the whole batch. This
            // copy is a local, released when the worker moves to the next file.
            const src = cloudSrc[i];
            // Fail fast if the local file can't be read (changed / moved / renamed since it was added, its card or
            // drive removed, or online-only in iCloud Drive / Dropbox). Reading one byte of the ORIGINAL forces a
            // disk touch; a NotReadableError / NotFoundError here is PERMANENT, so skip the UPLOAD_ATTEMPTS network
            // retries (~7.5s per dead file) — and check before the resize, whose failure would otherwise read as
            // "this browser can't make previews".
            if (!src) {
              const rerr = await readError(files[i]);
              if (control.cancelled) return;
              if (gen[i] !== g) continue;
              if (rerr) { failUnreadable(i, rerr, g); continue; }
            }
            let file: File;
            try {
              file = src ? files[i] : await resizeForUpload(i);
            } catch (err) {
              if (control.cancelled) return;
              if (err instanceof PreviewCopyError) { failCopy(i, err, g); continue; }
              // Never let a worker die with its file unaccounted for: it fails with the reason.
              console.warn(`[upload] ${files[i]?.name} couldn't be prepared: ${errorName(err)}`, err);
              if (settle(i, g)) patch(job.id, (j) => failRow(j, i, failKey("internal", errorName(err)), `Couldn't prepare this file (${errorName(err)})`), true);
              posts[i] = null;
              continue;
            }
            if (control.cancelled) return;
            if (gen[i] !== g) continue;
            patch(job.id, (j) => { const fp = j.files[i]; if (fp && fp.status === "queued") fp.status = "uploading"; });
            // Upload the content with a bounded retry. Each attempt gets a fresh
            // AbortController (registered for cancel); a partial attempt rolls its
            // counted bytes back before the next try so job counters stay accurate.
            let uploaded = false;
            let skippedNow = false;
            let lastUploadError = "";
            if (post.multipart) {
              // Gated video multipart path (parts uploaded + completed inside
              // uploadMultipart, which has its own per-part retry). No POST re-mint on
              // failure — a fresh plan would be needed — so a failed multipart file
              // falls to the normal "failed" state (the pane's Retry re-mints it).
              const mac = new AbortController();
              control.controllers.add(mac);
              try {
                await uploadMultipart(post.multipart, file, (fraction) => {
                  if (gen[i] !== g) return;
                  const now = Math.round(fraction * file.size);
                  const delta = now - sent[i];
                  if (delta > 0) {
                    sent[i] = now;
                    patch(job.id, (j) => {
                      j.bytesSent += delta;
                      const fp = j.files[i];
                      if (fp) { fp.loaded = now; fp.status = "uploading"; }
                    });
                  }
                }, mac.signal);
                uploaded = true;
              } catch {
                if (control.cancelled) return;
                if (sent[i] > 0) {
                  const partial = sent[i];
                  sent[i] = 0;
                  patch(job.id, (j) => { j.bytesSent -= partial; const fp = j.files[i]; if (fp) fp.loaded = 0; });
                }
              } finally {
                control.controllers.delete(mac);
              }
            } else {
            for (let attempt = 0; attempt < UPLOAD_ATTEMPTS && !control.cancelled; attempt += 1) {
              const ac = new AbortController();
              control.controllers.add(ac);
              try {
                const onProg = (fraction: number) => {
                  if (gen[i] !== g) return; // given up on: its bytes no longer count
                  const now = Math.round(fraction * file.size);
                  const delta = now - sent[i];
                  if (delta > 0) {
                    sent[i] = now;
                    patch(job.id, (j) => {
                      j.bytesSent += delta;
                      const fp = j.files[i];
                      if (fp) { fp.loaded = now; fp.status = "uploading"; }
                    });
                  }
                };
                // Egress Phase 2: an R2-targeted mint returns a single presigned PUT
                // (`post.put`) instead of the S3 POST form — R2 has no POST Object API.
                // Same resolve/reject contract, so the retry + re-mint path is identical.
                // Dropbox / Google Drive: the server copies the file straight into this target.
                if (src) {
                  try {
                    await transferFromCloud(src.provider, src.id, post as never, ac.signal, src.workspace ? { workspace: src.workspace } : undefined);
                  } catch (cerr) {
                    if (cerr instanceof CloudReconnectError) {
                      if (!reconnectShown) {
                        reconnectShown = true;
                        toast({
                          title: `Reconnect ${PROVIDER_LABELS[cerr.provider]}`,
                          description: `${PROVIDER_LABELS[cerr.provider]} needs you to sign in again — use “From cloud” to reconnect, then retry the failed photos.`,
                          variant: "destructive",
                        });
                      }
                      attempt = UPLOAD_ATTEMPTS; // permanent until reconnected — don't retry
                    }
                    throw cerr;
                  }
                  onProg(1);
                  uploaded = true;
                  break;
                }
                const direct = () => (post.put
                  ? uploadPutPresigned(post.put, file, onProg, ac.signal)
                  : uploadToPresigned(post, file, onProg, ac.signal));
                // Upload Worker first (HTTP/2+3 through Cloudflare's edge); on any failure other than
                // a cancel, fall straight back to the presigned upload from the same mint — without
                // spending an attempt — so the Worker can only ever make an upload faster.
                if (viaWorker && post.worker && uploadWorkerWanted()) {
                  try {
                    await uploadViaWorker(post.worker, file, onProg, ac.signal);
                    noteUploadWorker(true);
                  } catch (werr) {
                    if (control.cancelled || (werr instanceof DOMException && werr.name === "AbortError")) throw werr;
                    noteUploadWorker(false);
                    if (sent[i] > 0) {
                      const partial = sent[i];
                      sent[i] = 0;
                      patch(job.id, (j) => { j.bytesSent -= partial; const fp = j.files[i]; if (fp) fp.loaded = 0; });
                    }
                    await direct();
                  }
                } else {
                  await direct();
                }
                uploaded = true;
                break;
              } catch (err) {
                // A cancel aborted this PUT — leave the count alone (job is terminal).
                if (control.cancelled) return;
                lastUploadError = err instanceof Error ? err.message : String(err);
                // Roll back this attempt's partial bytes so a retry (or the final
                // failure) doesn't leave the job over-counted.
                if (sent[i] > 0) {
                  const partial = sent[i];
                  sent[i] = 0;
                  patch(job.id, (j) => {
                    j.bytesSent -= partial;
                    const fp = j.files[i];
                    if (fp) fp.loaded = 0;
                  });
                }
                // A presigned S3 POST expires after a while; on a long 100k job a URL
                // minted ahead of the uploader can 403 before its turn. Re-mint a fresh
                // (plain) target for THIS file so the next attempt uploads to a live URL
                // rather than retrying the dead one. Best-effort — fall through to retry.
                // Offline: wait for the network to come back, and don't spend an attempt —
                // a dropped Wi-Fi mid-run shouldn't fail every in-flight file.
                if (typeof navigator !== "undefined" && navigator.onLine === false) {
                  await waitOnline(control.sig);
                  if (control.cancelled) return;
                  attempt -= 1;
                  continue;
                }
                const msg = err instanceof Error ? err.message : String(err);
                if (/\(40[13]\)/.test(msg) && attempt < UPLOAD_ATTEMPTS - 1) {
                  const fresh = await mintWithRetry([files[i]], [paths[i]], false, false);
                  if (fresh === "cancelled") return;
                  if (fresh?.urls[0]?.skip) { skippedNow = true; break; } // original landed meanwhile
                  // null = re-mint failed — retry the existing URL and let it fail out if truly dead
                  if (fresh?.urls[0]) { post = fresh.urls[0]; posts[i] = post; }
                }
                // Exponential backoff with jitter so a transient 5xx/network blip mid-run
                // doesn't thundering-herd on retry.
                if (attempt < UPLOAD_ATTEMPTS - 1)
                  await sleep(UPLOAD_BACKOFF_MS * 2 ** attempt + Math.floor(Math.random() * 250));
              } finally {
                control.controllers.delete(ac);
              }
            }
            }
            if (control.cancelled) return;
            if (gen[i] !== g) continue; // given up on while it uploaded: its answer no longer counts
            if (skippedNow) { markSkipped(i, g); continue; }
            // Safari sends a file that was deleted / renamed after it was added as an EMPTY body — and the upload
            // "succeeds" (seen on Safari 26.6, repro-safari-file-reads). Bytes straight from disk (not a copy made
            // here) are only trusted if the file still reads afterwards; otherwise it fails as unreadable.
            if (uploaded && !src && file === files[i]) {
              const after = await readError(files[i]);
              if (control.cancelled) return;
              if (after) { failUnreadable(i, after, g); posts[i] = null; continue; }
            }
            if (!settle(i, g)) { posts[i] = null; continue; } // already counted (the watchdog gave up on it)
            if (uploaded) {
              // Ensure the file counts as fully sent even if no final progress event.
              const remainder = file.size - sent[i];
              patch(job.id, (j) => {
                if (remainder > 0) j.bytesSent += remainder;
                j.completed += 1;
                const fp = j.files[i];
                if (fp) { fp.loaded = fp.size; fp.status = "done"; }
              }, !fastPreviews); // throttled, like finishUploaded
              // Surface landed files in the remote pane progressively (throttled).
              liveRefreshContent();
              // Crop-back: the server will want this copy's face / person crops cut from the original.
              if (cropBackOn[i] && !src && job.previewFirst === "copy") {
                cropBackTrackFor(scope).track(job.engineGuid, post.contentGuid ?? contentGuids[i], files[i]);
              } else if (job.previewFirst === "original") {
                // Its original is in: the server cuts any crops it still needs from that itself.
                cropBackTrackFor(scope).untrack(post.contentGuid ?? contentGuids[i]);
              }
            } else {
              patch(job.id, (j) => failRow(
                j, i,
                failKey("upload", lastUploadError ? uploadErrorName(lastUploadError) : post?.multipart ? "multipart" : "unknown"),
                lastUploadError ? `Upload failed: ${lastUploadError}` : undefined,
              ), true);
            }
            // Done with file i — free its presigned URL (~1-2 KB each × 100k ≈ 150 MB).
            // The resized copy is a local (`file`) and is released on the next iteration.
            posts[i] = null;
            } finally {
              if (inflight.get(i)?.g === g) inflight.delete(i);
            }
          }
          } finally {
            if (slot.alive) active -= 1;
            slot.alive = false;
          }
        };

        // Keep `target` workers running. Each spawn synchronously bumps `active` (the
        // worker runs `active += 1` before its first await), so this can't over-spawn
        // within a tick. Scale-down is handled by workers hitting their retire check.
        const workerSlots = new WeakMap<Promise<void>, WorkerSlot>();
        const spawn = () => {
          const slot: WorkerSlot = { alive: true };
          const p = uploadWorker(slot).finally(() => workers.delete(p));
          workers.add(p);
          workerSlots.set(p, slot);
        };
        // Wait for the workers to finish — but never on one the watchdog gave up on, and not past the moment every
        // file has its outcome (a request that never answers can't hold the pass open).
        const abandoned = new Signal();
        const drain = async () => {
          for (;;) {
            if (settledCount >= n && !retry.length) return;
            const live = [...workers].filter((p) => workerSlots.get(p)?.alive !== false);
            if (!live.length) return;
            await Promise.race([Promise.allSettled(live), whenAllSettled(), abandoned.wait()]);
          }
        };
        // Files queued for a re-send (`retry`) once the claiming is over: one by one, never spinning.
        const sendRetries = async () => {
          while (retry.length && !control.cancelled) {
            const before = retry.length;
            for (let k = 0; k < Math.min(retry.length, Math.max(1, target)); k += 1) spawn();
            await drain();
            if (retry.length >= before && !control.cancelled) {
              // Nothing picked them up (can't happen today): fail them with a reason rather than loop or hang.
              failMany(retry.splice(0), "internal:not-sent", "Not sent — the uploader couldn't get to it. Retry it.");
              break;
            }
          }
        };
        const ensureWorkers = () => {
          // MUST re-check `!control.cancelled`: a cancelled uploadWorker does `active += 1` then,
          // on its first `if (control.cancelled) return`, runs `finally { active -= 1 }` SYNCHRONOUSLY
          // before any await — net zero on `active`. Without this guard, a cancel that lands during
          // the adaptive controller's `await sleep` (the loop then calls ensureWorkers once more) spins
          // this while forever: `active < target` never flips, `uploadCursor` never advances, and each
          // iteration allocates a promise + `.finally` closure → the tab freezes with CPU at 100% and
          // the heap thrashing GC. (Profiled: 25s self-time in `() => workers.delete(p)`.)
          while (active < target && uploadCursor < n && !control.cancelled) spawn();
        };

        // Adaptive controller: sample achieved throughput each interval and hill-climb
        // `target` toward the throughput knee for this connection. Dithering hill-climb
        // — whenever a change makes throughput worse, reverse direction — so it settles
        // ±1 around the optimum and re-adapts if the link speeds up or slows down.
        const runUploadsAdaptive = async () => {
          ensureWorkers(); // seed the initial pool at CONC_START
          const smallMax = fastPreviews ? SMALL_CONC_MAX : SMALL_CONC_MAX_LEGACY;
          const hi = Math.min(cloudJob ? CLOUD_CONC : smallFiles ? smallMax : CONC_MAX, n);
          const lo = Math.min(cloudJob ? CLOUD_CONC : CONC_MIN, n);
          let lastBytes = jobsRef.current.get(job.id)?.bytesSent ?? 0;
          let lastT = perfNow();
          let lastRate = 0;
          let tuner: ConcTuner = { target, dir: 1, ewma: 0, ref: 0 };
          while (uploadCursor < n && !control.cancelled) {
            await sleep(CONC_SAMPLE_MS);
            // While paused, freeze the tuner: re-baseline so the idle interval isn't
            // read as a throughput collapse, and show a steady/0 MB/s snapshot.
            if (control.paused) {
              lastBytes = jobsRef.current.get(job.id)?.bytesSent ?? lastBytes;
              lastT = perfNow();
              lastRate = 0;
              tuner = { target, dir: tuner.dir, ewma: 0, ref: 0 }; // re-baseline after the pause
              patch(job.id, (j) => {
                if (j.conc) j.conc = { workers: target, trend: "steady", bytesPerSec: 0 };
              });
              continue;
            }
            // Always keep the pool seeded — critical on RESUME: pausing zeroes lastRate,
            // and the hill-climb's ensureWorkers() below only runs when lastRate > 0, so
            // without this a resumed batch whose workers had drained would never restart.
            ensureWorkers();
            const nowBytes = jobsRef.current.get(job.id)?.bytesSent ?? lastBytes;
            const nowT = perfNow();
            const dt = (nowT - lastT) / 1000;
            const rate = dt > 0 ? Math.max(0, (nowBytes - lastBytes) / dt) : 0;
            let trend: ConcState["trend"] = "steady";
            if (lastRate > 0 || tuner.ewma > 0) {
              const prev = target;
              // Bundles carry BUNDLE_FILES files each: far fewer requests in flight are needed.
              const top = bundleTarget ? Math.min(hi, Math.max(lo, BUNDLE_CONC_MAX)) : hi;
              tuner = nextConcurrency({ ...tuner, target }, rate, {
                lo, hi: top, small: smallFiles && !bundleTarget, probeUp: viaWorker && uploadWorkerWanted(),
              });
              target = tuner.target;
              trend = target > prev ? "up" : target < prev ? "down" : "steady";
              ensureWorkers();
            } else {
              tuner = nextConcurrency({ ...tuner, target }, rate, {
                lo, hi, small: smallFiles && !bundleTarget, probeUp: false,
              }); // first sample: seed the smoothed baseline
            }
            // Publish what the tuner is doing so the UI can explain it in plain terms.
            patch(job.id, (j) => {
              j.conc = { workers: target, trend, bytesPerSec: rate };
            });
            lastRate = rate;
            lastBytes = nowBytes;
            lastT = nowT;
          }
          await drain(); // all files claimed → drain in-flight uploads
          // Files a bundle didn't get acked after the other workers had retired: send them one by one.
          await sendRetries();
          // Files that didn't go through for a reason that can pass: again, after a pause, re-minted.
          await autoRetry();
          patch(job.id, (j) => { j.conc = undefined; }, true); // tuning done — clear the banner
        };

        // Scan pump: scan EVERY file on-device (independent of the upload frontier), then
        // (once that file has minted, so its contentGuid is known) request its crop/sidecar
        // targets and upload them. Any failure just leaves the deferred job to fall back to a
        // server scan — it can never fail or delay the content upload.
        const scanWorker = async () => {
          for (;;) {
            if (!scanOn || !serverPrescan) return; // scanning off/unavailable → server scans everything
            if (control.cancelled) return;
            // Hold before claiming the next file to scan while paused.
            if (control.paused) {
              await holdWhilePaused();
              if (control.cancelled) return;
            }
            const i = scanIdx;
            if (i >= n) return;
            scanIdx += 1;
            const ac = new AbortController();
            control.controllers.add(ac);
            try {
              // Scan the SAME deterministic downscale the upload uses (shared by reference via
              // the resize cache when both tracks overlap on i, else computed here), so detection
              // boxes and the stored image share one coordinate space.
              const file = await sharedResize(i);
              if (control.cancelled) return;
              patch(job.id, (j) => { const fp = j.files[i]; if (fp) fp.scan = "scanning"; });
              const scan = await scanFileForUpload(file, wantPeople, wantFaces); // best-effort; null = server-scan
              if (!scanOn || !serverPrescan) return; // latched off while we were scanning
              if (control.cancelled) return;
              if (!scan) {
                // On-device scan unavailable/failed → the server scans this photo.
                scanLog(`file[${i}] ${files[i]?.name ?? ""}: on-device scan returned null → server will scan`);
                patch(job.id, (j) => { const fp = j.files[i]; if (fp) fp.scan = "skipped"; });
              } else {
                // Record the on-device detection counts, then upload crops + sidecar.
                scanLog(
                  `file[${i}] ${files[i]?.name ?? ""}: detected`,
                  { faces: scan.faces.length, people: scan.people.length },
                );
                patch(job.id, (j) => {
                  const fp = j.files[i];
                  if (fp) { fp.scan = "detected"; fp.faces = scan.faces.length; fp.people = scan.people.length; }
                });
                // Need the server contentGuid (from the mint) to address this object's targets.
                await presignGates[i];
                if (control.cancelled) return;
                const contentGuid = contentGuids[i];
                if (!contentGuid) {
                  // No content object to attach to (its mint failed) → server scans it.
                  patch(job.id, (j) => { const fp = j.files[i]; if (fp) fp.sidecar = "skipped"; });
                } else {
                  const { targets, prescanAvailable } = await requestPrescanTargets(
                    scope,
                    job.engineGuid,
                    job.folder,
                    [{ contentGuid, faceCount: scan.faces.length, personCount: scan.people.length }],
                    ac.signal,
                  );
                  if (!prescanAvailable) {
                    scanLog(
                      `file[${i}] ${files[i]?.name ?? ""}: prescan targets refused (prescanAvailable=false)`,
                      "→ latching on-device scan OFF for the rest of this batch; server scans",
                    );
                    patch(job.id, (j) => { const fp = j.files[i]; if (fp) fp.sidecar = "skipped"; });
                    serverPrescan = false;
                    scanOn = false;
                    return;
                  }
                  if (targets[0]) {
                    patch(job.id, (j) => { const fp = j.files[i]; if (fp) fp.sidecar = "uploading"; });
                    const ok = await uploadPrescanArtifacts(scan, targets[0], ac.signal); // crops + sidecar
                    scanLog(`file[${i}] ${files[i]?.name ?? ""}: sidecar + crops upload ${ok ? "OK" : "FAILED → server scans"}`);
                    patch(job.id, (j) => { const fp = j.files[i]; if (fp) fp.sidecar = ok ? "done" : "failed"; });
                  }
                }
              }
            } catch {
              // scan / artifact upload failed → the photo falls back to a server scan.
              patch(job.id, (j) => {
                const fp = j.files[i];
                if (fp) {
                  if (fp.scan === "scanning") fp.scan = "skipped";
                  if (fp.sidecar === "uploading") fp.sidecar = "failed";
                }
              });
            } finally {
              control.controllers.delete(ac);
            }
            if (control.cancelled) return;
            // Count every file processed (scanned or fallen back) so the progress
            // reaches the total and the "scanning" state clears when it's really done.
            scanCompleted += 1;
            patch(job.id, (j) => { j.scanCompleted = scanCompleted; });
          }
        };

        // ---- Automatic retry ---------------------------------------------------------------------------------
        // Files that failed for a reason that can pass (autoRetryable) go again after a pause — RE-MINTED, so the
        // server answers "already there" for any whose original landed meanwhile (another tab, another device, a
        // server-side release) and those count as done. Up to the rounds in timing; whatever still fails is
        // reported with its reason. Runs before the pass counts as finished, so the status keeps saying so.
        let retryWaiting = false;
        const autoRetry = async () => {
          const delays = job.previewFirst === "copy" ? timing.copyRetryDelaysMs : timing.retryDelaysMs;
          const allowMultipart = job.kind === "video" && multipartEnabled();
          try {
            for (let round = 0; round < delays.length; round += 1) {
              if (control.cancelled || control.blocked || control.previewFirstRefused || unreadableStop) return;
              const j0 = jobsRef.current.get(job.id);
              if (!j0) return;
              const again: number[] = [];
              j0.files.forEach((fp, i) => { if (fp.status === "error" && settled[i] && files[i] && autoRetryable(fp.reason)) again.push(i); });
              if (!again.length) return;
              console.info(`[upload] batch ${job.id}: ${again.length} file(s) didn't go through — trying again in ${Math.round(delays[round] / 1000)} s (round ${round + 1} of ${delays.length})`);
              patch(job.id, (j) => { j.retrying = { count: again.length, round: round + 1, rounds: delays.length, at: Date.now() + delays[round] }; }, true);
              retryWaiting = true;
              try {
                const until = Date.now() + delays[round];
                while (!control.cancelled && Date.now() < until) {
                  await Promise.race([sleep(Math.max(0, until - Date.now())), control.sig.wait()]);
                  await holdWhilePaused();
                }
                await waitOnline(control.sig);
              } finally {
                retryWaiting = false;
              }
              if (control.cancelled) return;
              // Back in the queue: their failures (and reasons) are un-counted until they fail again.
              for (const i of again) { unsettle(i); sent[i] = 0; posts[i] = null; }
              patch(job.id, (j) => {
                for (const i of again) unfailRow(j, i);
                if (j.retrying) j.retrying = { ...j.retrying, at: undefined };
              }, true);
              for (let s0 = 0; s0 < again.length && !control.cancelled; s0 += MINT_BATCH) {
                const idx = again.slice(s0, s0 + MINT_BATCH);
                const minted = await mintWithRetry(idx.map((i) => files[i]), idx.map((i) => paths[i]), false, allowMultipart);
                if (minted === "cancelled") return;
                if (minted === null || !minted.urls || minted.urls.length < idx.length) {
                  failMany(
                    idx,
                    minted === null ? (control.blocked ? failKey("plan", lastMintName) : failKey("mint", lastMintName)) : "mint:short",
                    `Couldn't get an upload slot from the server${lastMintError ? ` (${lastMintError})` : ""}`,
                  );
                  continue;
                }
                idx.forEach((i, k) => {
                  posts[i] = minted.urls[k];
                  contentGuids[i] = minted.urls[k].contentGuid ?? contentGuids[i];
                  if (job.previewFirst === "copy") cropBackOn[i] = minted.cropBack === true;
                  retry.push(i);
                });
              }
              await sendRetries();
            }
          } finally {
            patch(job.id, (j) => { j.retrying = undefined; }, true);
          }
        };

        // ---- Stall watchdog (see STALL_KICK_MS) -----------------------------------------------------------------
        // Progress = bytes sent, a file settled, a mint answered, a photo scanned, a re-send taken. None for
        // timing.stallKickMs while files are held (or the scan is all that's left) → abort the requests in flight
        // (they retry). None for another stallKickMs → give up on the held files: each fails as "stalled" (the
        // automatic retry re-mints and re-sends it), its worker's place is freed and its late answer ignored; a
        // scan track that's stuck is handed to the server's scan. Paused, offline, signed out or between retries
        // isn't a stall.
        let contentDone = false;
        let scanGiveUp: () => void = () => {};
        const scanGaveUp = new Promise<void>((r) => { scanGiveUp = r; });
        let lastSig = "";
        let bytesHigh = 0;
        let quietSince = Date.now();
        let kicked = false;
        const watchdog = window.setInterval(() => {
          if (control.cancelled) return;
          const j = jobsRef.current.get(job.id);
          if (!j) return;
          // Bytes count as progress only past their high-water mark: an attempt rolled back and re-sent isn't progress.
          bytesHigh = Math.max(bytesHigh, j.bytesSent);
          const sigNow = `${bytesHigh}|${settledCount}|${mintIdx}|${scanCompleted}|${retry.length}`;
          const offline = typeof navigator !== "undefined" && navigator.onLine === false;
          if (sigNow !== lastSig || control.paused || j.waitingForSignIn || offline || retryWaiting) {
            lastSig = sigNow;
            quietSince = Date.now();
            kicked = false;
            return;
          }
          const held = [...inflight.entries()].filter(([i, h]) => gen[i] === h.g && !settled[i]);
          const scanStuck = contentDone && scanOn && serverPrescan && scanCompleted < n;
          if (!held.length && !scanStuck) { quietSince = Date.now(); kicked = false; return; }
          if (Date.now() - quietSince < timing.stallKickMs) return;
          const quietS = Math.round((Date.now() - quietSince) / 1000);
          quietSince = Date.now();
          if (!kicked) {
            kicked = true;
            console.warn(`[upload] batch ${job.id}: no progress for ${quietS} s — retrying the ${control.controllers.size} request(s) in flight`);
            for (const ac of [...control.controllers]) { try { ac.abort(); } catch { /* already settled */ } }
            return;
          }
          kicked = false;
          if (held.length) {
            console.warn(`[upload] batch ${job.id}: still no progress — setting ${held.length} stalled file(s) aside to retry`);
            const give: number[] = [];
            for (const [i, h] of held) {
              gen[i] += 1; // its worker's late answer no longer counts
              inflight.delete(i);
              if (h.w.alive) { h.w.alive = false; active -= 1; }
              posts[i] = null;
              if (settle(i)) give.push(i);
            }
            patch(job.id, (jj) => {
              for (const i of give) {
                if (sent[i] > 0) { jj.bytesSent -= sent[i]; sent[i] = 0; }
                const fp = jj.files[i];
                if (fp) fp.loaded = 0;
                failRow(jj, i, failKey("upload", "stalled"), "Upload stalled: no progress for several minutes. It's tried again automatically.");
              }
            }, true);
            abandoned.notify();
          }
          if (scanStuck) {
            console.warn(`[upload] batch ${job.id}: on-device scanning stopped making progress — our servers scan the rest`);
            scanOn = false;
            serverPrescan = false;
            scanGiveUp();
          }
        }, timing.watchdogTickMs);
        stopWatchdog = () => window.clearInterval(watchdog);

        // Sold first (preview-first): the manager's sold poll (every few seconds while any preview-first pass
        // runs in this gallery) asks each pass what to do with photos a buyer just ordered whose original
        // hasn't gone yet — see src/lib/uploader/soldPriority.ts. A copy pass sends those originals NOW as
        // a prioritised batch (the originals pass that follows skips them); the main originals pass hands
        // its still-queued sold files to one; a prioritised batch only holds its own. Whatever no pass
        // holds is reported as missing from this upload session.
        // "Originals later": a copy pass that promised its originals for later sends none now (not even sold ones).
        if (job.previewFirst && job.kind === "image" && !job.originalsBy) {
          const pipeline = job.pairId ?? job.id;
          const bumpNow = (idx: number[]) => {
            const args = jobInputs.current.get(job.id)?.args;
            if (!idx.length || !args || control.cancelled) return;
            enqueueRef.current({
              ...args,
              files: idx.map((i) => files[i]),
              paths: idx.map((i) => paths[i]),
              previewFirst: "original",
              resize: undefined,
              scan: undefined,
              prioritise: true,
              soldFirst: idx.length,
              pairId: pipeline,
            });
          };
          soldHandlers.current.set(job.id, {
            scope,
            scopeKey: job.scopeKey,
            engineGuid: job.engineGuid,
            folder: job.folder,
            onSold: (sold) => {
              const live = jobsRef.current.get(job.id);
              if (!live || control.cancelled || !sold.size) return new Set();
              let plan: SoldPlan;
              if (job.previewFirst === "copy") {
                let sent = sentOriginals.current.get(pipeline);
                if (!sent) { sent = new Set(); sentOriginals.current.set(pipeline, sent); }
                plan = planCopyPass(paths, live.files, sold, sent);
                for (const i of plan.bump) sent.add(paths[i]);
              } else if (job.seq >= 0) {
                plan = planOriginalsPass(paths, live.files, sold, uploadCursor, handedOff);
                for (const i of plan.bump) handedOff.add(i);
              } else {
                plan = planHoldOnly(paths, live.files, sold);
              }
              bumpNow(plan.bump);
              return plan.claimed;
            },
            // The same photos added again from another folder: a sold photo's key isn't in this pass, but a file
            // with its name is — send that file into the SOLD photo's key (only when unambiguous). Copy and main
            // originals passes only; a prioritised batch just holds its own files.
            onSoldByName: job.seq >= 0 ? (missing, all) => {
              const live = jobsRef.current.get(job.id);
              const args = jobInputs.current.get(job.id)?.args;
              if (!live || !args || control.cancelled || !missing.length) return new Set();
              let sent = sentByName.current.get(pipeline);
              if (!sent) { sent = new Set(); sentByName.current.set(pipeline, sent); }
              const picks = planSoldByName(paths, live.files, missing, all, job.folder, sent);
              for (const m of picks) sent.add(m.key);
              if (picks.length) {
                enqueueRef.current({
                  ...args,
                  files: picks.map((m) => files[m.index]),
                  paths: picks.map((m) => m.key),
                  previewFirst: "original",
                  resize: undefined,
                  scan: undefined,
                  prioritise: true,
                  soldFirst: picks.length,
                  pairId: pipeline,
                });
              }
              return new Set(missing.filter((s) => sent!.has(s.key)).map((s) => s.key));
            } : undefined,
          });
          setSoldTick((t) => t + 1);
        }

        // Let a cancel release this batch's big per-file structures the instant it's clicked —
        // without waiting for the pumps to unwind. A cancelled 100k batch otherwise kept the mint
        // targets, gate promises and the shared resized-copy cache live long enough for the GC to
        // thrash the tab. Length-preserving / null-filling so any coroutine still mid-teardown reads
        // a null (handled exactly like a failed mint) rather than going out of bounds.
        control.dispose = () => {
          try {
            for (const r of presignResolvers) r?.(); // idempotent — never leave an awaiter hung
            posts.fill(null);
            contentGuids.fill(null);
            cropBackOn.fill(false);
            resizeShare.clear();
            handedOff.clear();
            projected.clear();
          } catch { /* best-effort — teardown never throws */ }
        };

        await Promise.all([
          // Every file settled = minting is over for this pass, even if a mint request never answered.
          Promise.race([mintPump(), whenAllSettled()]),
          runUploadsAdaptive().then(() => {
            contentDone = true;
            // Content upload is done — but on-device scanning (crops/sidecars) may
            // still be running. Reflect that instead of a stuck "uploading" spinner.
            contentDoneRef.current.get(job.id)?.();
            patch(job.id, (j) => {
              if (!control.cancelled) j.contentDoneAt ??= Date.now();
              if (
                !control.cancelled &&
                scanOn &&
                serverPrescan &&
                (j.scanTotal ?? 0) > 0 &&
                (j.scanCompleted ?? 0) < (j.scanTotal ?? 0)
              ) {
                j.phase = "scanning";
              }
            }, true);
          }),
          // The scan track — unless the watchdog hands a stuck one to the server's scan.
          Promise.race([Promise.all(Array.from({ length: scanCoroutineCount() }, scanWorker)), scanGaveUp]),
        ]);

        diagMark("runJob:pumps-settled", `cancelled=${control.cancelled}`);
        patch(job.id, (j) => {
          j.finishedAt = Date.now();
          if (control.cancelled) { j.phase = "cancelled"; j.paused = false; j.conc = undefined; return; }
          j.phase = j.failed > 0 && j.completed === 0 ? "error" : "done";
        }, true);
        diagMark("runJob:terminal-patched");
        scanLog(
          `batch ${job.id} complete`,
          {
            uploaded: files.length,
            onDeviceScan: scanOn && serverPrescan ? "active" : "off (server scanned)",
            scanCompleted,
            scanTotal: scanOn ? files.length : 0,
            cancelled: control.cancelled,
          },
        );

        // Refresh the gallery's photo list wherever it's mounted (and the video
        // list too when this was a video batch — keyed by slug, so prefix-match).
        // Even a cancelled batch may have landed some files, so still refresh.
        // Two content query families exist: the paged ["content", ...] (gallery
        // Photos tab) AND the "load everything" ["content-all", ...] that the
        // uploader's REMOTE pane reads (useAllContent). Both must be invalidated —
        // invalidating only ["content", ...] left the right pane stale, so freshly
        // uploaded files never appeared there until a manual reload.
        if (trailingTimer != null) { window.clearTimeout(trailingTimer); trailingTimer = null; }
        const refreshContent = () => {
          queryClient.invalidateQueries({ queryKey: ["content", job.scopeKey, job.engineGuid] });
          queryClient.invalidateQueries({ queryKey: ["content-all", job.scopeKey, job.engineGuid] });
          if (job.kind === "video") queryClient.invalidateQueries({ queryKey: ["videos", job.scopeKey] });
        };
        diagMark("runJob:refreshContent");
        refreshContent();
        diagMark("runJob:refreshContent-done");
        // The object is ingested server-side asynchronously AFTER the PUT returns
        // (typically 5–15s, longer for a video), so an immediate refetch misses the
        // last files. Re-invalidate across that window so the final files show up.
        for (const ms of SETTLE_REFRESH_MS) setTimeout(refreshContent, ms);
      } catch (err) {
        console.warn(`[upload] batch ${job.id} stopped: ${errorName(err)}`, err);
        patch(job.id, (j) => {
          if (control.cancelled) { j.phase = "cancelled"; j.paused = false; j.conc = undefined; return; }
          j.phase = "error";
          const rest = j.total - j.completed - j.failed;
          if (rest > 0) noteFail(j, null, failKey("internal", errorName(err)), rest);
          j.failed = j.total - j.completed;
        }, true);
      } finally {
        // Batch is terminal — stop the heartbeat, report the final state once (so it
        // drops off the active-uploads list), and release the scan pool if idle.
        diagMark("runJob:finally-enter");
        window.clearInterval(reportTimer);
        stopWatchdog();
        if (soldHandlers.current.delete(job.id)) setSoldTick((t) => t + 1);
        contentDoneRef.current.get(job.id)?.(); // early exits never skipped a waiting hand-off
        contentDoneRef.current.delete(job.id);
        notifyFailures(job.id, control.cancelled);
        reportSession();
        releaseScanPoolIfIdle();
        control.dispose?.(); // release the batch's big per-file structures now it's terminal
        diagMark("runJob:finally-done");
      }
    },
    [patch, queryClient, releaseScanPoolIfIdle, waitForSignIn, cropBackTrackFor, notifyFailures],
  );

  const enqueue = useCallback(
    ({ scope, engineGuid, galleryName, files, kind = "image", folder, scan, resize, prioritise, paths, previewFirst: pf, previewSize, pairId, soldFirst: soldLead, originalsBy }: EnqueueArgs) => {
      const prefix = kind === "video" ? "video/" : "image/";
      const accepted: File[] = [];
      const acceptedPaths: string[] = [];
      files.forEach((f, k) => {
        if (!f.type.startsWith(prefix)) return;
        accepted.push(f);
        acceptedPaths.push(paths?.[k] || relativePath(f));
      });
      if (accepted.length === 0 || !engineGuid) return;
      // Preview-first is photos-only: a copy pass uploads the quick copy (and scans it); an
      // original pass uploads untouched with no scan (the server already scanned the copy).
      // Cloud imports go up untouched (the server copies the original; there are no bytes here
      // to resize or scan), so they never resize, scan or run preview-first.
      const fromCloud = accepted.some((f) => !!cloudSourceOf(f));
      const previewFirst = kind === "image" && !fromCloud ? pf : undefined;
      const jobResize = fromCloud ? undefined : previewFirst === "copy" ? previewCopyResize(previewSize) : previewFirst === "original" ? undefined : resize;
      const jobScan = fromCloud || previewFirst === "original" ? undefined : scan;
      const job: UploadJob = {
        id: nextId(),
        galleryName: galleryName || "gallery",
        engineGuid,
        scopeKey: scopeKeyOf(scope),
        total: accepted.length,
        completed: 0,
        failed: 0,
        bytesTotal: accepted.reduce((n, f) => n + f.size, 0),
        bytesSent: 0,
        phase: "presigning",
        startedAt: Date.now(),
        kind,
        folder,
        // Scanning + resize only apply to photo batches.
        scan: kind === "image" ? jobScan : undefined,
        resize: kind === "image" ? jobResize : undefined,
        previewFirst,
        files: accepted.map((f) => ({ name: f.name, size: f.size, loaded: 0, status: "queued" as const })),
        seq: prioritise ? --prioSeq : normalSeq++,
      };
      // Timeline link: a copy pass heads its own pipeline; its originals (and their sold-first / retry
      // batches, which re-enqueue with these args) carry the copy pass's id.
      if (previewFirst) job.pairId = pairId ?? (previewFirst === "copy" ? job.id : undefined);
      if (soldLead) job.soldFirst = soldLead;
      if (previewFirst === "copy" && originalsBy) job.originalsBy = originalsBy;
      jobsRef.current.set(job.id, job);
      jobInputs.current.set(job.id, { args: { scope, engineGuid, galleryName, files: [], kind, folder, scan, resize, previewFirst, previewSize, pairId: job.pairId, originalsBy: job.originalsBy }, files: accepted, paths: acceptedPaths });
      controlsRef.current.set(job.id, {
        paused: false,
        cancelled: false,
        controllers: new Set<AbortController>(),
        sig: new Signal(),
      });
      flush();
      const contentDone = new Promise<void>((resolve) => contentDoneRef.current.set(job.id, resolve));
      const run = runJob(job, scope, accepted, acceptedPaths);
      // "Originals later": only the copies — the originals are added later (any device), from the owed list.
      if (previewFirst !== "copy" || job.originalsBy) return;
      // Copies uploaded → queue the originals of every copy that landed (same keys, untouched,
      // appended — not prioritised), sold photos first. Chained on the CONTENT uploads, not the job:
      // the job also waits for on-device scanning of every copy, which can take as long as the
      // upload (or hang), and the originals don't need it — the server keys them to the copy's
      // photo. A cancelled pass doesn't chain; its landed copies are offered for finishing by the
      // uploader's "waiting for originals" banner.
      void Promise.race([contentDone, run]).then(async () => {
        const j = jobsRef.current.get(job.id);
        if (!j || j.phase === "cancelled" || controlsRef.current.get(job.id)?.cancelled) return;
        const pending = await listPendingOriginals(scope, engineGuid, folder);
        // Originals the sold poll already sent ahead of the queue during the copy pass are not sent again.
        const sent = sentOriginals.current.get(j.pairId ?? job.id);
        const idx: number[] = [];
        j.files.forEach((fp, i) => { if (fp.status === "done" && !fp.skipped && !sent?.has(acceptedPaths[i])) idx.push(i); });
        if (!idx.length) return;
        const sold = new Set((pending ?? []).filter((p) => p.sold).map((p) => p.key));
        const order = soldFirst(idx, (i) => acceptedPaths[i], sold);
        enqueueRef.current({
          scope, engineGuid, galleryName, kind, folder,
          files: order.map((i) => accepted[i]),
          paths: order.map((i) => acceptedPaths[i]),
          previewFirst: "original",
          pairId: j.pairId,
          soldFirst: idx.filter((i) => sold.has(acceptedPaths[i])).length,
        });
      });
    },
    [flush, runJob],
  );
  useEffect(() => { enqueueRef.current = enqueue; }, [enqueue]);

  const dismiss = useCallback((id: string) => {
    jobsRef.current.delete(id);
    controlsRef.current.delete(id);
    jobInputs.current.delete(id);
    flush();
  }, [flush]);

  const clearFinished = useCallback(() => {
    for (const [id, j] of jobsRef.current) {
      if (j.phase === "done" || j.phase === "error" || j.phase === "cancelled") {
        jobsRef.current.delete(id);
        controlsRef.current.delete(id);
        jobInputs.current.delete(id);
      }
    }
    flush();
  }, [flush]);

  const pause = useCallback((id?: string) => {
    const one = (jid: string) => {
      const c = controlsRef.current.get(jid);
      if (!c || c.cancelled) return;
      c.paused = true;
      c.sig.notify();
      patch(jid, (j) => { if (isRunning(j)) j.paused = true; }, true);
    };
    if (id) one(id);
    else for (const jid of controlsRef.current.keys()) one(jid);
  }, [patch]);

  const resume = useCallback((id?: string) => {
    const one = (jid: string) => {
      const c = controlsRef.current.get(jid);
      if (!c || c.cancelled) return;
      c.paused = false;
      c.sig.notify();
      patch(jid, (j) => { j.paused = false; }, true);
    };
    if (id) one(id);
    else for (const jid of controlsRef.current.keys()) one(jid);
  }, [patch]);

  const cancelJob = useCallback((id: string) => {
    diagMarkReset("cancel:");
    diagMark("cancel:enter", id.slice(0, 8));
    const c = controlsRef.current.get(id);
    if (c) {
      c.cancelled = true;
      c.paused = false; // release any paused loops so they observe the cancel and exit
      diagMark("cancel:abort-controllers", `n=${c.controllers.size}`);
      for (const ac of c.controllers) { try { ac.abort(); } catch { /* already settled */ } }
      diagMark("cancel:aborted");
      c.sig.notify();
      // Drop this batch's big per-file structures NOW, not when the pumps finish unwinding — a
      // cancelled 100k batch otherwise held them (and the resized copies they reference) long
      // enough for the GC to thrash. The pumps see `cancelled` and exit; the null-filled arrays
      // are read exactly like a failed mint.
      c.dispose?.();
      diagMark("cancel:disposed");
    }
    patch(id, (j) => {
      // Only running jobs become cancelled; already-terminal jobs stay as they are.
      if (j.phase === "done" || j.phase === "error" || j.phase === "cancelled") return;
      j.phase = "cancelled";
      j.paused = false;
      j.conc = undefined;
      j.finishedAt = Date.now();
    }, true);
    diagMark("cancel:patched");
    // Tear down the scan workers now (if no other batch is running) so their memory
    // is reclaimed immediately — and any scan awaiting a worker unblocks and stops —
    // rather than waiting for the drained job to settle. This is what makes
    // cancelling a large batch stop hard instead of freezing the tab.
    releaseScanPoolIfIdle();
    diagMark("cancel:scanpool-released");
    // Diagnostics: snapshot the heap + holders now and for 20s after, to see whether the
    // retained bytes actually release once the batch is torn down (gated, no-op by default).
    uploadDiagCancelBurst(id.slice(0, 8));
  }, [patch, releaseScanPoolIfIdle]);

  const cancelAll = useCallback(() => {
    for (const id of [...controlsRef.current.keys()]) cancelJob(id);
  }, [cancelJob]);

  const retryFailed = useCallback((id: string) => {
    const j = jobsRef.current.get(id);
    const input = jobInputs.current.get(id);
    if (!j || !input || isRunning(j)) return;
    const files: File[] = [];
    const paths: string[] = [];
    j.files.forEach((fp, i) => {
      if (fp.status === "error" && input.files[i]) {
        files.push(input.files[i]);
        paths.push(input.paths[i]);
      }
    });
    if (!files.length) return;
    dismiss(id);
    enqueue({ ...input.args, files, paths, prioritise: true });
  }, [dismiss, enqueue]);

  const activeCount = jobs.filter((j) => j.phase === "presigning" || j.phase === "uploading").length;

  // Diagnostics: run a periodic heap/holder snapshot while a batch is active (gated, no-op by default).
  useEffect(() => {
    if (activeCount > 0) uploadDiagStart("active");
    else uploadDiagStop("idle");
  }, [activeCount]);

  // Warn before leaving/refreshing while uploads are still running.
  useEffect(() => {
    if (activeCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [activeCount]);

  // Hold a screen wake lock while a batch is uploading, so a long (100k / overnight)
  // run isn't throttled or paused when the machine would otherwise sleep. The lock is
  // released by the OS if the tab is hidden, so re-acquire on visibility regain. All
  // best-effort — unsupported browsers simply upload as before (and resume covers a
  // slipped sleep). `navigator.wakeLock` isn't in older lib.dom typings, hence the cast.
  useEffect(() => {
    if (activeCount === 0) return;
    const wl = (navigator as unknown as {
      wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> };
    }).wakeLock;
    if (!wl) return;
    let sentinel: { release: () => Promise<void> } | null = null;
    const acquire = async () => {
      try {
        if (document.visibilityState === "visible" && !sentinel) sentinel = await wl.request("screen");
      } catch {
        /* denied / not allowed — ignore */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void acquire();
      else sentinel = null; // OS auto-releases when hidden
    };
    void acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      sentinel?.release?.().catch(() => {});
      sentinel = null;
    };
  }, [activeCount]);

  // Preview-first sold poll: one loop per gallery that has a running preview-first pass. Every SOLD_POLL_MS
  // (or the server's pollMs) it reads the gallery's sold-but-owed photos — a tiny sparse index; every
  // FULL_LIST_EVERY-th poll reads the full owed list, which also covers photos sold before that index
  // existed — and hands them to each pass (see the handlers in runJob). Errors back off; the loop stops
  // once no pass runs in the gallery.
  const soldGroups = useMemo(() => {
    const groups = new Map<string, { scope: TenantScope; scopeKey: string; engineGuid: string }>();
    for (const h of soldHandlers.current.values()) groups.set(`${h.scopeKey}|${h.engineGuid}`, { scope: h.scope, scopeKey: h.scopeKey, engineGuid: h.engineGuid });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soldTick]);
  const soldGroupKeys = [...soldGroups.keys()].sort().join(",");
  useEffect(() => {
    const timers: number[] = [];
    const aborts: AbortController[] = [];
    let alive = true;
    for (const [gk, g] of soldGroups) {
      let polls = 0;
      let failures = 0;
      let base = SOLD_POLL_MS;
      const round = async () => {
        if (!alive) return;
        const handlers = [...soldHandlers.current.values()].filter((h) => `${h.scopeKey}|${h.engineGuid}` === gk);
        // Between one pass ending and the next registering (copy pass → its originals pass) there can be a moment
        // with no handler while the gallery's group key stays the same — keep the loop alive instead of ending
        // it (the effect only restarts when the key changes, so returning here stopped the sold poll for good).
        if (!handlers.length) {
          if (alive) timers.push(window.setTimeout(() => void round(), base));
          return;
        }
        const ac = new AbortController();
        aborts.push(ac);
        try {
          const full = polls % FULL_LIST_EVERY === 0;
          const res = await listSoldOriginals(g.scope, g.engineGuid, { full, signal: ac.signal });
          polls += 1;
          failures = 0;
          if (res.pollMs && res.pollMs >= 1000 && res.pollMs <= 60_000) base = res.pollMs;
          // A backend without the sparse sold index answers the whole owed list (can be thousands of
          // photos mid-upload) — ask it at the old once-a-minute pace instead of every few seconds.
          else if (!full && !res.sparse) base = LEGACY_SOLD_POLL_MS;
          if (!alive) return;
          const sold: SoldOriginal[] = res.sold;
          const claims: Array<readonly [string | undefined, Set<string>]> = [];
          for (const h of handlers) {
            const keys = soldKeysFor(sold, h.folder);
            claims.push([h.folder, keys.size ? h.onSold(keys) : new Set<string>()]);
          }
          let missing = missingLocally(sold, claims);
          // Not held by path anywhere: a pass may hold the same photo under another folder (same file name).
          for (const h of handlers) {
            if (!missing.length || !h.onSoldByName) continue;
            const byName = h.onSoldByName(missing, sold);
            if (byName.size) missing = missing.filter((s) => !byName.has(s.key));
          }
          setSoldWaiting((prev) => {
            const was = prev[gk];
            if (was && sameSold(was.missing, missing) && sameSold(was.sold, sold)) return prev;
            return { ...prev, [gk]: { engineGuid: g.engineGuid, scopeKey: g.scopeKey, missing, sold, checkedAt: Date.now() } };
          });
        } catch {
          failures += 1;
        }
        if (alive) timers.push(window.setTimeout(() => void round(), nextSoldPollDelay(failures, base)));
      };
      void round();
    }
    // Galleries whose passes all ended: drop their notice (the uploader's idle check takes over).
    setSoldWaiting((prev) => {
      const keep = Object.fromEntries(Object.entries(prev).filter(([k]) => soldGroups.has(k)));
      return Object.keys(keep).length === Object.keys(prev).length ? prev : keep;
    });
    return () => {
      alive = false;
      timers.forEach((t) => window.clearTimeout(t));
      aborts.forEach((a) => a.abort());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soldGroupKeys]);

  const value = useMemo(
    () => ({
      jobs, soldWaiting, activeCount, enqueue, dismiss, clearFinished, pause, resume, cancelJob, cancelAll, retryFailed,
      sessionExpired, signInAgain,
    }),
    [jobs, soldWaiting, activeCount, enqueue, dismiss, clearFinished, pause, resume, cancelJob, cancelAll, retryFailed, sessionExpired, signInAgain],
  );

  return <UploadManagerContext.Provider value={value}>{children}</UploadManagerContext.Provider>;
};

export const useUploadManager = () => {
  const ctx = useContext(UploadManagerContext);
  if (!ctx) throw new Error("useUploadManager must be used within UploadManagerProvider");
  return ctx;
};
